/* build:1785185968 V3.34f-UX1 */
/**
 * ParaDermia™ — Quiz Diagnostic Algorithm (13 steps)
 * Pure JavaScript, zero runtime dependencies.
 * Runs identically in Node (tests) and browser (Shopify theme).
 *
 * Usage:
 *   const output = ParaDermiaAlgo.run(profile, catalogue);
 *
 * A `catalogue` is an array of product objects shaped like /assets/catalogue-v5.json.
 * A `profile` is the full set of quiz answers collected by quiz-paradermia.js.
 *
 * Offline mode = pass the full catalogue and let every `available` default to true.
 * Online mode  = quiz-paradermia.js fetches Storefront API once (batch), maps inventory
 *                onto the catalogue, then calls run(profile, cataloguePlusStock).
 */
(function (root) {
  'use strict';

  // ────────────────────────────────────────────────────────────────────────────
  //  CONSTANTS
  // ────────────────────────────────────────────────────────────────────────────

  const PACKS = ['pack_CLEAR','pack_CALM','pack_EVEN','pack_GLOW','pack_REPAIR','pack_LIFT','pack_SAFE','pack_STRETCH'];
  const LEVELS = ['niveau_STARTER','niveau_OPTIMAL','niveau_PREMIUM'];
  const LEVEL_ORDER = { niveau_STARTER: 0, niveau_OPTIMAL: 1, niveau_PREMIUM: 2 };

  // ────────────────────────────────────────────────────────────────────────────
  //  Mapping client-facing — JAMAIS le mot "pack" dans les textes affichés.
  // ────────────────────────────────────────────────────────────────────────────
  const ROUTINE_DISPLAY = {
    pack_CLEAR:   { name: 'Routine CLEAR',   descriptor: 'Acné & Imperfections' },
    pack_CALM:    { name: 'Routine CALM',    descriptor: 'Sensibilité & Rougeurs' },
    pack_EVEN:    { name: 'Routine EVEN',    descriptor: 'Taches & Pigmentation' },
    pack_GLOW:    { name: 'Routine GLOW',    descriptor: 'Éclat & Anti-fatigue' },
    pack_REPAIR:  { name: 'Routine REPAIR',  descriptor: 'Sécheresse & Barrière' },
    pack_LIFT:    { name: 'Routine LIFT',    descriptor: 'Anti-âge & Fermeté' },
    pack_SAFE:    { name: 'Routine SAFE',    descriptor: 'Grossesse & Allaitement' },
    pack_STRETCH: { name: 'Routine STRETCH', descriptor: 'Vergetures (corps)' }
  };

  // FEATURE 3 — couleur primaire/secondaire par Routine pour theming page résultats.
  const PACK_COLORS = {
    pack_CLEAR:   { primary: '#88cc40', secondary: '#5aa820' },
    pack_CALM:    { primary: '#a8d5e2', secondary: '#7ab8c8' },
    pack_EVEN:    { primary: '#C9A84C', secondary: '#e8c96a' },
    pack_GLOW:    { primary: '#d4a820', secondary: '#e8c040' },
    pack_REPAIR:  { primary: '#c8a882', secondary: '#a87850' },
    pack_LIFT:    { primary: '#9b8ec4', secondary: '#7b6aa4' },
    pack_SAFE:    { primary: '#88cc40', secondary: '#C9A84C' },
    pack_STRETCH: { primary: '#d4809a', secondary: '#c87890' }
  };

  const NIVEAU_DISPLAY = {
    niveau_STARTER: 'STARTER',
    niveau_OPTIMAL: 'OPTIMAL',
    niveau_PREMIUM: 'PREMIUM'
  };

  // V3.34f — Jokers HINTS (préférence, pas vérité). Le catalogue live est
  // la source unique : si l'ID hint existe dans le catalogue → on l'utilise ;
  // sinon findJoker fallback en piochant dynamiquement le produit le plus
  // "safe" du step (safe_grossesse > apaisant > réparateur > premier dispo).
  // Résultat : plus aucune étape skippée à cause d'un ID fantôme obsolète.
  const JOKER_HINTS = {
    etape_demaquillant: 'PD-0485',
    etape_nettoyant:    'PD-0394',
    etape_toner:        'PD-0443',
    etape_serum:        'PD-0479',
    etape_hydratant:    'PD-0410',
    etape_spf:          'PD-0458',
    etape_spf_ivvi:     'PD-0377',
    etape_zone_specifique: 'PD-0426'
  };

  // Tags jamais affichés côté client (BUG 3 — filtrage strict UI).
  // Liste noire par préfixe + valeurs exactes pour les tags techniques orphelins.
  const CLIENT_TAG_BLACKLIST_PREFIXES = [
    'INCI_', 'profil_', 'pack_', 'niveau_', 'etape_', 'addon_',
    'famille_actif_', 'role_', 'usage_', 'type_', 'phototype_',
    'mode_vie_', 'complement_', 'nav_', 'menu_', 'tier_',
    'eye_concern_', 'zone_yeux_', 'spf_', 'texture_', 'intensite_',
    'contient_', 'contre_indique_', 'pd-', 'PD-', 'score_'
  ];
  const CLIENT_TAG_BLACKLIST_EXACT = [
    'joker_universel', 'INCI_a_confirmer', 'SLES',
    'fragrance_free', 'safe_grossesse', 'parfum_present',
    'homme_uniquement', 'conditionnel_grossesse'
  ];

  // Libellés propres pour les actifs affichables côté client.
  const ACTIF_DISPLAY = {
    actif_BHA: 'BHA', actif_PHA: 'PHA',
    actif_mandelic: 'acide mandélique', actif_glycolic: 'acide glycolique',
    actif_retinal: 'rétinal',            // ← JAMAIS "rétinol" pour rétinaldéhyde
    actif_retinol: 'rétinol',
    actif_bakuchiol: 'bakuchiol',
    actif_vitC: 'Vitamine C',            actif_niacinamide: 'niacinamide',
    actif_azelaique: 'acide azélaïque',  actif_tranexamique: 'acide tranexamique',
    actif_arbutine: 'arbutine',          actif_melasyl: 'Melasyl',
    actif_thiamidol: 'Thiamidol',        actif_peptides: 'peptides',
    actif_ceramides: 'céramides',        actif_panthenol: 'panthénol',
    actif_squalane: 'squalane',          actif_PDRN: 'PDRN',
    actif_centella: 'centella',          actif_madecassoside: 'madécassoside',
    actif_trepenone: 'trépénone',        actif_bisabolol: 'bisabolol',
    actif_zinc: 'zinc',                  actif_cafeine: 'caféine',
    actif_HA: 'acide hyaluronique',      actif_allantoine: 'allantoïne',
    peau_noire_metissee: 'Adapté peaux noires et métissées'
  };
  function sanitizeClientTag(tag) {
    if (!tag) return null;
    if (CLIENT_TAG_BLACKLIST_EXACT.indexOf(tag) !== -1) return null;
    for (var i = 0; i < CLIENT_TAG_BLACKLIST_PREFIXES.length; i++) {
      if (tag.indexOf(CLIENT_TAG_BLACKLIST_PREFIXES[i]) === 0) {
        // Exception : actif_* passe (après transformation)
        if (CLIENT_TAG_BLACKLIST_PREFIXES[i] !== 'type_' && tag.indexOf('actif_') === 0) {
          return ACTIF_DISPLAY[tag] || tag.replace('actif_', '');
        }
        return null;
      }
    }
    return tag;
  }
  // CORRECTION 1+2 — exclut les tags d'intensité (actif_doux/modere/fort,
  // intensite_*) et déduplique avec Set pour éviter "niacinamide, niacinamide".
  const TAGS_INTENSITE_BLACKLIST = [
    'actif_doux','actif_modere','actif_fort',
    'intensite_douce','intensite_moderee','intensite_forte'
  ];
  function displayActifs(actifList) {
    if (!Array.isArray(actifList)) return [];
    return [...new Set(
      actifList
        .filter(t => TAGS_INTENSITE_BLACKLIST.indexOf(t) === -1)
        .map(a => ACTIF_DISPLAY[a] || a.replace('actif_', ''))
        .filter(Boolean)
    )];
  }

  // Anti-doublons compléments (ordre de priorité descendant).
  const COMPLEMENT_DUP_RULES = {
    zinc:        ['PD-0084','PD-0089','PD-309'],
    omega3:      ['PD-0085','PD-0310'],
    collagene:   ['PD-0086','PD-0068','PD-311'],
    carotenoide: ['PD-0091','PD-0312']
  };

  // Mapping Q2.3 → pack principal.
  const Q23_TO_PACK = {
    A: 'pack_CLEAR',  B: 'pack_EVEN',    C: 'pack_LIFT',   D: 'pack_GLOW',
    E: 'pack_CALM',   F: 'pack_REPAIR',  G: 'pack_SAFE',   H: 'pack_STRETCH'
  };

  // Mapping Q7.2 → pack secondaire.
  const Q72_TO_SECONDARY = { C: 'pack_EVEN', E: 'pack_GLOW', F: 'pack_LIFT' };

  // Ordre d'affichage routine (BLOCK 2).
  // Sprint 2 — 'etape_traitement' inséré après 'etape_serum' (slot conditionnel).
  const STEP_ORDER = [
    'etape_demaquillant',
    'etape_nettoyant',
    'etape_toner',
    'etape_exfoliant',
    'etape_serum',
    'etape_traitement',
    'etape_hydratant',
    'etape_spf',
    'etape_zone_specifique'
  ];

  // Sprint 4c — Mapping Q7_COMPLEMENTS option → tags compléments dormants.
  // Pattern dormant cohérent avec Sprint 4b complement_probiotiques et Sprint 4b
  // inci_* : le catalogue actuel ne porte pas ces tags, la déduplication s'active
  // automatiquement quand la session catalogue les appliquera aux produits.
  const COMPLEMENTS_PRIS_MAP = {
    A: ['complement_magnesium'],
    B: ['complement_omega3', 'complement_omega3_algue'],
    C: ['complement_vitD'],
    D: ['complement_zinc'],
    E: ['complement_vitB', 'complement_b12'],
    F: ['complement_probiotiques'],
    G: ['complement_vitC'],
    H: ['complement_collagene'],
    I: ['complement_ashwagandha']
    // J = aucun, pas de mapping
  };

  // INCI tags à exclure pendant la grossesse/allaitement (§6 GROSSESSE).
  const PREGNANCY_EXCLUDES = [
    'contient_retinol',
    'contre_indique_grossesse',
    'contient_acide_fort',    // exception: actif_PHA autorisé
    'contient_alcool_fort',
    'INCI_EO_Phototoxic',
    'parfum_present'
  ];

  // ────────────────────────────────────────────────────────────────────────────
  //  HELPERS
  // ────────────────────────────────────────────────────────────────────────────

  const hasTag = (product, tag) => product.tags && product.tags.indexOf(tag) !== -1;
  const hasAny = (product, tags) => tags.some(t => hasTag(product, t));
  const hasAll = (product, tags) => tags.every(t => hasTag(product, t));
  const isAvailable = (product) => product.available !== false; // default true offline

  // Sprint 4b/4c — helpers profil multi-select. Q7_MEDICAMENTS, Q7_ALLERGIES,
  // Q7_COMPLEMENTS sont des arrays de codes d'option stockés en p.medicaments,
  // p.allergies_alimentaires, p.complements_pris respectivement (Sprint 3).
  const hasMedicament = (p, code) => Array.isArray(p.medicaments) && p.medicaments.indexOf(code) !== -1;
  const hasAllergy = (p, code) => Array.isArray(p.allergies_alimentaires) && p.allergies_alimentaires.indexOf(code) !== -1;
  const hasComplementPris = (p, code) => Array.isArray(p.complements_pris) && p.complements_pris.indexOf(code) !== -1;

  function sortByTier(products, pack) {
    return [...products].sort((a, b) => {
      const ta = (a.tiers && a.tiers[pack]) || 99;
      const tb = (b.tiers && b.tiers[pack]) || 99;
      return ta - tb;
    });
  }

  function capLevel(level, maxLevel) {
    return LEVEL_ORDER[level] > LEVEL_ORDER[maxLevel] ? maxLevel : level;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 1 — Collecte inputs quiz
  // ────────────────────────────────────────────────────────────────────────────

  function collectInputs(answers) {
    const a = answers || {};
    // Q2.3b supprimée (CORRECTION 2) — le pack secondaire est dérivé de Q7.2 uniquement.
    // V3.34f BUG-G — Override skin_type si Q2_3='F' (peau sèche préoccupation
    // principale) et Q2_1='D' (sébum confortable). Sans override, l'algo
    // catégorise 'profil_peau_normale' → filtres peau sèche (BUG-A nettoyant
    // doux, boost hydratant réparateur) ne s'activent pas. Une utilisatrice
    // qui déclare "peau très sèche" comme problème principal DOIT recevoir la
    // logique peau sèche même si son sébum en journée est confortable
    // (déshydratation vs sécheresse constitutionnelle).
    let _skinType = mapSkinType(a.Q2_1);
    if (a.Q2_3 === 'F' && _skinType !== 'profil_peau_seche') {
      _skinType = 'profil_peau_seche';
    }
    const p = {
      // Diagnostic socle
      skin_type:         _skinType,
      barrier_status:    mapBarrier(a.Q2_2),
      primary_concern:   a.Q2_3,
      acne_type:         a.Q2_3acne || null,
      secondary_concern: null, // conservé pour compat mais toujours vide
      score_duree:       scoreDuree(a.Q2_4),
      q2_5:              a.Q2_5 || null,
      is_pregnant:       a.Q2_6 === 'A',
      is_breastfeeding:  a.Q2_6 === 'B',
      // STEP 7
      age_group:         mapAge(a.Q7_1),
      // V3.34f — Q7_2 remonté en STEP2 (posé tous parcours). Filtre 'H' pour
      // ignorer "aucun objectif secondaire" comme choix positif.
      secondary_objectives: (function() {
        const raw = Array.isArray(a.Q7_2) ? a.Q7_2 : (a.Q7_2 ? [a.Q7_2] : []);
        return raw.filter(v => v && v !== 'H');
      })(),
      eye_concern:       a.Q7_2bis || null,
      tolerance:         a.Q7_3 || null,
      rosacee:           a.Q7_3_rosacee === true,
      sensitivities:     Array.isArray(a.Q7_3b) ? a.Q7_3b : [],
      phototype_group:   mapPhototype(a.Q7_4),
      uv_exposure:       a.Q7_4bis === 'B' ? 'forte' : 'faible_moderee',
      post_medical:      a.Q7_5 || 'C',
      existing_actives:  Array.isArray(a.Q7_6) ? a.Q7_6 : (a.Q7_6 ? [a.Q7_6] : []),
      preference_soin:   a.Q7_7 || null,
      gender:            mapGender(a.Q7_8),
      rasage:            a.Q7_9 || null,
      port_maquillage:   a.Q7_10 === 'A' || a.Q7_10 === 'B',
      // STEP MDV — V3.34f fix : QMDV valeurs 'A'-'F' → tags mode_vie_* pour que
      // le boost lifestyle des compléments (complementTagForLifestyle) fonctionne.
      // Avant : p.lifestyle=['A','B','C'] → complementTagForLifestyle('A') retourne 'A'
      // (fallback) qui n'est jamais un tag produit → boost muet. Fix : mapping explicite.
      lifestyle: (function() {
        const MDV_MAP = { A: 'mode_vie_sommeil', B: 'mode_vie_stress', C: 'mode_vie_alimentation', D: 'mode_vie_sport', E: 'mode_vie_soleil' };
        const raw = Array.isArray(a.QMDV) ? a.QMDV.slice(0, 3) : [];
        return raw.map(v => MDV_MAP[v]).filter(Boolean);
      })(),
      // Sprint 3 — 9 nouveaux signaux cliniques. Collecte passive jusqu'à Sprint 4.
      taches_type:              a.Q2_3taches || null,
      pih_history:              a.Q7_PIH || null,
      hormonal_context:         a.Q7_HORMONAL || null,
      tabac:                    a.Q7_TABAC || null,
      medicaments:              Array.isArray(a.Q7_MEDICAMENTS) ? a.Q7_MEDICAMENTS : (a.Q7_MEDICAMENTS ? [a.Q7_MEDICAMENTS] : []),
      pratiques_eclaircissants: a.Q7_PRATIQUES || null,
      regularite_routine:       a.Q7_REGULARITE || null,
      allergies_alimentaires:   Array.isArray(a.Q7_ALLERGIES) ? a.Q7_ALLERGIES : (a.Q7_ALLERGIES ? [a.Q7_ALLERGIES] : []),
      complements_pris:         Array.isArray(a.Q7_COMPLEMENTS) ? a.Q7_COMPLEMENTS : (a.Q7_COMPLEMENTS ? [a.Q7_COMPLEMENTS] : [])
    };
    p.score_expertise = scoreExpertise(p.existing_actives);
    // STEP 5 — choix utilisateur (override sous plafond). Stocké côté UI sous
    // forme 'STARTER'/'OPTIMAL'/'PREMIUM' ; on convertit en 'niveau_X' interne.
    const ts = a.tier_selected;
    p.tier_override = (ts && LEVELS.indexOf('niveau_' + ts) !== -1) ? 'niveau_' + ts : null;

    // Sprint 4a — Flags pigmentation / PIH (cf décisions validées).
    //   phototype_at_risk_pih = catégorie sémantique (notice 6.5, pédagogie).
    //   pih_strict_active et pih_moderate_active mutuellement exclusifs :
    //     le cas (III + pih ∈ B,D) tombe en moderate, pas en strict.
    const _pih = p.pih_history;
    const _isPihBD = (_pih === 'B' || _pih === 'D');
    const _isPhotoIVVI = (p.phototype_group === 'phototype_IV_V_VI');
    const _isPhotoIII = (p.phototype_group === 'phototype_III');
    p.phototype_at_risk_pih = _isPhotoIVVI || (_isPhotoIII && (_pih === 'A' || _isPihBD));
    p.pih_strict_active = (_pih === 'A') || (_isPihBD && _isPhotoIVVI);
    p.pih_moderate_active = _isPihBD && _isPhotoIII;

    return p;
  }

  function mapSkinType(q) {
    return { A: 'profil_peau_grasse', B: 'profil_peau_mixte', C: 'profil_peau_seche', D: 'profil_peau_normale' }[q] || null;
  }
  function mapBarrier(q) {
    if (q === 'A') return 'profil_barriere_fragile';
    if (q === 'D') return 'profil_barriere_fragile_reactive';
    return 'profil_barriere_normale';
  }
  function scoreDuree(q) { return { A: 0, B: 1, C: 2, D: 3 }[q] || 0; }
  function mapAge(q) {
    return { A: 'age_moins_18', B: 'age_18_24', C: 'age_25_34', D: 'age_35_44', E: 'age_45_54', F: 'age_55_plus' }[q] || 'age_25_34';
  }
  function mapPhototype(q) {
    if (q === 'A' || q === 'B') return 'phototype_I_II';
    if (q === 'C') return 'phototype_III';
    if (q === 'D' || q === 'E' || q === 'F') return 'phototype_IV_V_VI';
    return 'phototype_III';
  }
  function mapGender(q) { return q === 'B' ? 'profil_homme' : 'profil_femme'; }

  // Sprint 4d Phase 3 — Refonte additive pure avec plafond Math.min(s, 3).
  //
  // Justification clinique du fix :
  //   - VitC L-ascorbique haute concentration (10-20%) n'est PAS un actif grand
  //     public. Sa tolérance et son protocole d'application (pH bas, fenêtre AM
  //     stricte, sensibilité initiale fréquente) demandent une habitude réelle.
  //     Elle mérite un point d'expertise au même titre que les acides exfoliants
  //     ou le rétinol débutant. L'ancienne version retournait 0 pour vitC seule,
  //     ce qui sous-estimait le niveau d'expertise réel de la cliente.
  //   - Les deux caps explicites supprimés (acides+rétinol_gte3m → 2 ; acides+vitC
  //     → 1) bridaient artificiellement le score additif et créaient une
  //     incohérence : une cliente déclarant deux actifs concentrés se voyait
  //     attribuer le même score qu'une débutante.
  //   - Le plafond Math.min(s, 3) préserve la borne haute du score total qui
  //     pilote la sélection de niveau, pour éviter qu'un cumul d'actifs
  //     auto-déclaré pousse automatiquement en PREMIUM (couplé au score_duree
  //     déjà capé à 3, le total max reste 6 → PREMIUM légitime, pas saturé).
  function scoreExpertise(actives) {
    if (!actives || !actives.length) return 0;
    const hasAcids   = actives.indexOf('A') !== -1;
    const hasVitC    = actives.indexOf('B') !== -1;
    const hasRetLT3  = actives.indexOf('C_lt3m') !== -1;
    const hasRetGTE3 = actives.indexOf('C_gte3m') !== -1;
    let s = 0;
    if (hasAcids)   s += 1;
    if (hasVitC)    s += 1;
    if (hasRetLT3)  s += 1;
    if (hasRetGTE3) s += 2;
    return Math.min(s, 3);
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 2 — Niveau final
  // ────────────────────────────────────────────────────────────────────────────

  // Plafond maximum autorisé pour ce profil (sécurité absolue).
  // Renvoie 'niveau_STARTER' | 'niveau_OPTIMAL' | 'niveau_PREMIUM'.
  function maxAllowedLevel(p) {
    let cap = 'niveau_PREMIUM';
    // Sprint 1 C1 — Q7_5='A' OU 'B' (post-acte 0-2 sem ou récup pleine) → cap STARTER
    if (p.post_medical === 'A' || p.post_medical === 'B') cap = capLevel(cap, 'niveau_STARTER');
    // Sprint 4b R3.1 — Isotrétinoïne orale ou récemment arrêtée → cap STARTER
    if (hasMedicament(p, 'A'))                         cap = capLevel(cap, 'niveau_STARTER');
    // Sprint 4b R4.2 — Éclaircissants achetés sans prescription → cap OPTIMAL max
    if (p.pratiques_eclaircissants === 'B')            cap = capLevel(cap, 'niveau_OPTIMAL');
    if (p.age_group === 'age_moins_18')               cap = capLevel(cap, 'niveau_OPTIMAL');
    if (p.barrier_status === 'profil_barriere_fragile' ||
        p.barrier_status === 'profil_barriere_fragile_reactive')
                                                       cap = capLevel(cap, 'niveau_OPTIMAL');
    if (p.preference_soin === 'B' &&
       (p.barrier_status === 'profil_barriere_fragile' ||
        p.barrier_status === 'profil_barriere_fragile_reactive'))
                                                       cap = capLevel(cap, 'niveau_OPTIMAL');
    return cap;
  }

  // Niveau recommandé STRICT par l'algo (score + plafond), ignore tout override.
  function computeAlgoLevel(p) {
    const total = p.score_duree + p.score_expertise;
    const algoLevel = total <= 1 ? 'niveau_STARTER' : total <= 3 ? 'niveau_OPTIMAL' : 'niveau_PREMIUM';
    return capLevel(algoLevel, maxAllowedLevel(p));
  }

  // Niveau effectivement utilisé pour la sélection produits :
  //   - si l'utilisateur a explicitement choisi via STEP 5 → on respecte (clipped au plafond)
  //   - sinon → niveau algo (computeAlgoLevel)
  function computeLevel(p) {
    if (p.tier_override) {
      return capLevel(p.tier_override, maxAllowedLevel(p));
    }
    return computeAlgoLevel(p);
  }

  // Helper UI exposé : renvoie le niveau RECOMMANDÉ pour ce profil
  // (ignore tier_override volontairement — c'est ce qu'on conseille à la cliente,
  // pas ce qu'elle a déjà choisi). Accepte les `answers` bruts (pas un profil).
  function computeRecommendedLevel(answers) {
    return computeAlgoLevel(collectInputs(answers || {}));
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 3 — Pack principal (+ SAFE override, STRETCH split)
  // ────────────────────────────────────────────────────────────────────────────

  function determinePack(p) {
    const primary = Q23_TO_PACK[p.primary_concern] || 'pack_CALM';
    // Pack secondaire = 1er matching dans Q7.2 (C=EVEN, E=GLOW, F=LIFT).
    const secondary = (p.secondary_objectives || [])
      .map(v => Q72_TO_SECONDARY[v])
      .find(Boolean) || null;
    const res = {
      primary_pack: primary,
      secondary_pack: secondary,
      face_pack: primary,         // pack effectif pour routine visage
      body_pack: null,            // actif uniquement pour STRETCH
      safe_override: false
    };

    // STRETCH — corps uniquement, visage séparé
    if (primary === 'pack_STRETCH') {
      res.body_pack = 'pack_STRETCH';
      res.face_pack = (p.is_pregnant || p.is_breastfeeding) ? 'pack_SAFE' : 'pack_CALM';
      res.safe_override = (p.is_pregnant || p.is_breastfeeding);
    }

    // OVERRIDE SAFE global si grossesse/allaitement (sauf STRETCH déjà géré)
    if ((p.is_pregnant || p.is_breastfeeding) && primary !== 'pack_STRETCH') {
      res.primary_pack = 'pack_SAFE';
      res.face_pack = 'pack_SAFE';
      res.safe_override = true;
    }

    // Si le pack secondaire est incompatible grossesse → reset
    if (res.safe_override && res.secondary_pack === 'pack_LIFT') {
      res.secondary_pack = null; // LIFT impose rétinal = CI grossesse
    }
    // Sprint 4a Section 4.2 (note défensive) — Péri-ménopause (hormonal_context='E')
    // préserve naturellement secondary_pack='pack_LIFT' puisque safe_override=false
    // hors grossesse. Si une future règle de reset est ajoutée, elle devra exclure
    // explicitement hormonal_context='E' pour ne pas casser l'intention clinique.
    return res;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 4 — Alertes immédiates (affichées pendant le quiz)
  // ────────────────────────────────────────────────────────────────────────────

  function computeImmediateAlerts(p) {
    const alerts = [];
    if (p.rosacee) {
      alerts.push({
        type: 'rosacee',
        severity: 'info',
        message: 'Rougeurs persistantes détectées — nous vous recommandons un avis dermatologue en parallèle de votre routine. Votre routine sera adaptée : actifs doux et exfoliation PHA uniquement.'
      });
    }
    if (p.post_medical === 'A') {
      alerts.push({
        type: 'post_acte_actif',
        severity: 'warning',
        message: 'Votre peau est en récupération post-acte. Nous activons la Routine REPAIR minimaliste — tous actifs forts exclus.'
      });
    }
    if (p.primary_concern === 'A' &&
        (p.score_duree === 2 || p.score_duree === 3) &&
        p.q2_5 === 'C') {
      alerts.push({
        type: 'derma_recommande',
        severity: 'info',
        message: 'Votre acné semble sévère et persistante. La consultation d\'un dermatologue est fortement recommandée en complément de votre routine — certaines formes nécessitent un traitement médical (rétinoïdes oraux, antibiothérapie) que la cosmétique ne peut remplacer.'
      });
    }
    return alerts;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 5 — Pool produits par étape
  //  STEP 6 — Filtres INCI et profil (appliqués en même temps sur le pool)
  //  STEP 7 — Règles composites
  // ────────────────────────────────────────────────────────────────────────────

  function buildStepPool(step, pack, level, catalogue, p) {
    let pool = catalogue.filter(prod => {
      if (!isAvailable(prod)) return false;
      // Sprint stratégie homme CH.4 — Exclusion dure formats hors quiz :
      // format voyage, échantillon GWP, soin bébé. Belt-and-suspenders :
      // ces tags écartent le produit de TOUS les pools de sélection même
      // s'il porte par erreur un pack/etape/niveau qui le rendrait éligible.
      // addon_bebe est dormant (à appliquer par la session catalogue) ;
      // format_voyage et gwp_sample sont déjà présents au catalogue.
      if (hasTag(prod, 'format_voyage') || hasTag(prod, 'gwp_sample') || hasTag(prod, 'addon_bebe')) return false;
      // Sprint 2 — Éligibilité étendue : step principal OU compatible_steps.
      // Permet aux produits hybrides (ex. sérum LIFT pouvant servir de
      // traitement intensif) d'apparaître dans plusieurs pools.
      const matchesStep = (prod.step === step)
        || (Array.isArray(prod.compatible_steps) && prod.compatible_steps.indexOf(step) !== -1);
      if (!matchesStep) return false;

      // etape_zone_specifique transverse aux packs (pas de filtre pack_)
      if (step !== 'etape_zone_specifique') {
        if (!prod.packs || prod.packs.indexOf(pack) === -1) return false;
      }

      // addon_complement pilotés hors niveau (géré step 10, pas ici)
      if (step !== 'addon_complement' && prod.level && prod.level !== level) {
        return false;
      }
      return true;
    });

    pool = applyInciFilters(pool, p, step, pack);
    pool = applyCompositeRules(pool, p, step, pack);
    return pool;
  }

  function applyInciFilters(pool, p, step, pack) {
    const isLeaveOn = ['etape_serum','etape_hydratant','etape_spf','etape_toner','etape_zone_specifique'].includes(step);

    let filtered = pool.filter(prod => {
      // GROSSESSE / ALLAITEMENT — priorité absolue
      if (p.is_pregnant || p.is_breastfeeding) {
        for (const tag of PREGNANCY_EXCLUDES) {
          if (hasTag(prod, tag)) {
            // Exception : PHA est autorisé malgré contient_acide_fort
            if (tag === 'contient_acide_fort' && hasTag(prod, 'actif_PHA')) continue;
            return false;
          }
        }
      }

      // ÂGE < 18
      if (p.age_group === 'age_moins_18') {
        if (hasTag(prod, 'contient_retinol')) return false;
        if (hasTag(prod, 'zone_yeux_retinoide')) return false;
      }

      // PROFIL RÉACTIF ou ROSACÉE — leave-on uniquement
      if ((p.tolerance === 'A' || p.rosacee) && isLeaveOn) {
        if (hasTag(prod, 'contient_alcool_fort')) return false;
        if (hasTag(prod, 'INCI_EO_Phototoxic'))   return false;
      }

      // ROSACÉE — exfoliation = PHA uniquement
      if (p.rosacee && step === 'etape_exfoliant') {
        if (!hasTag(prod, 'actif_PHA')) return false;
      }

      // Sprint 1 C2 — Rosacée + sérum : exclusion rétinoïdes (aggravation poussées)
      if (p.rosacee && step === 'etape_serum') {
        if (hasAny(prod, ['famille_actif_retinoide', 'actif_retinol', 'actif_retinal', 'contient_retinol'])) return false;
      }

      // Sprint 1 C3 — Barrière fragile/réactive + nettoyant : exclusion BHA fort
      if (step === 'etape_nettoyant'
          && (p.barrier_status === 'profil_barriere_fragile' || p.barrier_status === 'profil_barriere_fragile_reactive')
          && hasTag(prod, 'famille_actif_bha') && hasTag(prod, 'actif_fort')) {
        return false;
      }

      // HANDOFF audit — Peau sèche / barrière fragile + nettoyant : exclusion des
      // nettoyants moussants (décapants) via regex sur le nom. Garantit un
      // nettoyant doux (lacté/eau). Le nettoyant doux PD-0394 (Novexpert
      // Lacté) couvre désormais tous les packs concernés (multi-packs).
      if (step === 'etape_nettoyant'
          && (p.skin_type === 'profil_peau_seche'
              || p.barrier_status === 'profil_barriere_fragile'
              || p.barrier_status === 'profil_barriere_fragile_reactive')
          && /mousse|moussant|foam/i.test(prod.name || '')) {
        return false;
      }

      // Sprint 4d Phase 4 R1 — Rasage régulier (Q7_9='A') + nettoyant : exclusion BHA fort.
      // Le rasage régulier fragilise la barrière par friction + micro-coupures.
      // Un BHA fort en nettoyant pénètre les micro-coupures et aggrave l'inflammation.
      // Conception ciblée volontaire (pas de cap général actif_doux/modere) : préserve
      // les nettoyants à actifs forts non-kératolytiques (azélaïque, niacinamide
      // concentrée) qui restent cliniquement pertinents même sur peau rasée.
      // Overlap volontaire avec C3 ci-dessus (rasage='A' + barrière fragile) :
      // double couche de sécurité, no-op effectif si les deux conditions tirent.
      if (step === 'etape_nettoyant' && p.rasage === 'A'
          && hasTag(prod, 'famille_actif_bha') && hasTag(prod, 'actif_fort')) {
        return false;
      }

      // PHOTOTYPE À RISQUE PIH — leave-on : pas d'AHA ni actif fort
      // Sprint 4a Section 5 — étendu de IV-VI seul à phototype_at_risk_pih
      // (= IV-VI OR III avec antécédent PIH personnel/flou).
      if (p.phototype_at_risk_pih && isLeaveOn) {
        if (hasTag(prod, 'famille_actif_aha')) return false;
        if (hasTag(prod, 'actif_fort'))        return false;
      }

      // IV-VI + exfoliation → mandelic ou PHA uniquement
      // Sprint 4d Phase 2 (note de gradation, NE PAS étendre à phototype_at_risk_pih) :
      // Cette règle reste volontairement scopée sur phototype IV-VI seul, pas
      // étendue à phototype_at_risk_pih. Justification clinique de la gradation :
      //   - Phototype IV-VI = risque PIH maximal → restriction maximale légitime
      //     (mandelic ou PHA only en exfoliant).
      //   - Phototype III + pih=A (systématique) = risque intermédiaire, déjà
      //     protégé par R2.4 strict qui exclut les AHA forts en exfoliant, mais
      //     conserve l'accès aux AHA modérés (glycolique faible, lactique modéré)
      //     qui restent des outils dépigmentants pertinents.
      //   - Phototype III + pih ∈ {B,D} (variable ou incertain) = risque bas,
      //     R2.5 modéré autorise les AHA forts en exfoliant nocturne lavé court.
      // Étendre cette règle à phototype_at_risk_pih contredirait R2.5 (test G
      // Sprint 4a) et serait sur-protecteur au point de retirer des options
      // thérapeutiques pertinentes à des profils à risque modéré.
      if (p.phototype_group === 'phototype_IV_V_VI' && step === 'etape_exfoliant') {
        if (!hasAny(prod, ['actif_mandelic','actif_PHA'])) return false;
      }

      // SANS PARFUM
      if ((p.sensitivities.indexOf('B') !== -1 || p.is_pregnant || p.is_breastfeeding) &&
          hasTag(prod, 'parfum_present')) return false;

      // GENRE
      if (p.gender === 'profil_femme' && hasTag(prod, 'homme_uniquement')) return false;

      // ALLERGIE SALICYLATES (si sensible)
      if (p.sensitivities.indexOf('A') !== -1 &&
          (p.tolerance === 'A' || p.tolerance === 'B') &&
          hasTag(prod, 'famille_actif_bha')) return false;

      // ──────────────────────────────────────────────────────────────────────
      //  SPRINT 4a — FILTRES PIGMENTATION / PIH par sous-type et antécédent
      // ──────────────────────────────────────────────────────────────────────

      // R2.1 — PIH post-acné (taches_type='A') sur sérum/exfoliant :
      // exclure AHA forts. Mandelic exempté (AHA doux).
      if (p.taches_type === 'A' && (step === 'etape_serum' || step === 'etape_exfoliant')) {
        if (hasTag(prod, 'actif_fort') && hasTag(prod, 'famille_actif_aha')
            && !hasTag(prod, 'actif_mandelic')) {
          return false;
        }
      }

      // R2.2 — Mélasma (taches_type='B') :
      //   sérum/exfoliant/toner : exclure vitC pure (acidifie, aggrave).
      //   exfoliant : exclure aussi actif_fort (peelings forts).
      if (p.taches_type === 'B') {
        if ((step === 'etape_serum' || step === 'etape_exfoliant' || step === 'etape_toner')
            && hasTag(prod, 'contient_vitc_pure')) {
          return false;
        }
        if (step === 'etape_exfoliant' && hasTag(prod, 'actif_fort')) {
          return false;
        }
      }

      // R2.4 — PIH strict (pih=A toutes peaux OU pih ∈ B,D + IV-VI) :
      //   sérum/exfoliant : exclure AHA forts.
      //   sérum : exclure rétinoïdes forts (retinol/retinal + actif_fort).
      if (p.pih_strict_active) {
        if (step === 'etape_serum' || step === 'etape_exfoliant') {
          if (hasTag(prod, 'actif_fort') && hasTag(prod, 'famille_actif_aha')) return false;
        }
        if (step === 'etape_serum') {
          if (hasTag(prod, 'actif_fort')
              && (hasTag(prod, 'actif_retinol') || hasTag(prod, 'actif_retinal'))) {
            return false;
          }
        }
      }

      // R2.5 — PIH modéré (phototype III + pih ∈ B,D) :
      //   sérum : exclure AHA forts ET vitC pure.
      //   exfoliant : pas de filtre (AHA forts autorisés en exfoliant).
      if (p.pih_moderate_active && step === 'etape_serum') {
        if (hasTag(prod, 'actif_fort') && hasTag(prod, 'famille_actif_aha')) return false;
        if (hasTag(prod, 'contient_vitc_pure')) return false;
      }

      // ──────────────────────────────────────────────────────────────────────
      //  SPRINT 4b — TTC + MÉDICAMENTS + PRATIQUES + ALLERGIES
      // ──────────────────────────────────────────────────────────────────────

      // Section 2 — Q7_HORMONAL='F' Projet de grossesse (TTC).
      // Filtre tératogène équivalent grossesse, sans forcer pack SAFE.
      if (p.hormonal_context === 'F'
          && (step === 'etape_serum' || step === 'etape_traitement' || step === 'etape_exfoliant')) {
        if (hasAny(prod, ['actif_retinol', 'actif_retinal', 'contient_retinol'])) return false;
        if (hasTag(prod, 'actif_fort') && hasTag(prod, 'famille_actif_bha')) return false;
        if (hasTag(prod, 'contient_alcool_fort')) return false;
        if (hasTag(prod, 'parfum_present')) return false;
      }

      // R3.1 — Isotrétinoïne (medicaments='A') : exclure actif_fort sur leave-on actifs.
      if (hasMedicament(p, 'A')
          && (step === 'etape_serum' || step === 'etape_traitement' || step === 'etape_hydratant')
          && hasTag(prod, 'actif_fort')) {
        return false;
      }

      // R3.2 — Anticoagulants (medicaments='B') : exclure actif_fort en exfoliant.
      if (hasMedicament(p, 'B') && step === 'etape_exfoliant' && hasTag(prod, 'actif_fort')) {
        return false;
      }

      // R4.2 — Éclaircissants sans prescription (pratiques='B') : actif_fort exclu
      // uniquement sur sérum, traitement et exfoliant — les actifs pénétrants
      // traitants où le risque cumul hydroquinone non encadrée ou corticoïdes
      // topiques est cliniquement pertinent. Hydratant, SPF, toner et zone yeux
      // sortent volontairement du périmètre : supports non pénétrants ou
      // photoprotection où le cumul n'a pas de pertinence clinique réelle.
      // _isLeaveOn4b reste défini plus bas (partagé avec la règle allergies).
      const _isPratiquesBPenetrants = step === 'etape_serum'
        || step === 'etape_traitement' || step === 'etape_exfoliant';
      if (p.pratiques_eclaircissants === 'B' && _isPratiquesBPenetrants && hasTag(prod, 'actif_fort')) {
        return false;
      }

      // _isLeaveOn4b — utilisé par la règle allergies alimentaires ci-dessous.
      // Couvre tous les leave-on où un allergène INCI déclenche le filtre.
      const _isLeaveOn4b = step === 'etape_serum' || step === 'etape_traitement'
        || step === 'etape_hydratant' || step === 'etape_spf' || step === 'etape_zone_specifique'
        || step === 'etape_toner';

      // R4.4 — Pratiques 'D' (préfère pas préciser) : conservatif, actif_fort exclu sérum.
      if (p.pratiques_eclaircissants === 'D' && step === 'etape_serum' && hasTag(prod, 'actif_fort')) {
        return false;
      }

      // Section 5 — Filtres allergies alimentaires (tags inci_* dormants).
      if (p.allergies_alimentaires && p.allergies_alimentaires.length && _isLeaveOn4b) {
        if (hasAllergy(p, 'A') && hasTag(prod, 'inci_avena')) return false;
        if (hasAllergy(p, 'B') && hasTag(prod, 'inci_glycine_soja')) return false;
        if (hasAllergy(p, 'C') && hasAny(prod, ['inci_argania', 'inci_prunus', 'inci_macadamia', 'inci_cocos', 'inci_juglans'])) return false;
        if (hasAllergy(p, 'D') && hasAny(prod, ['inci_lactose', 'inci_milk_protein', 'inci_lactic_acid_milk'])) return false;
      }

      return true;
    });

    // Priorités / dépriorités (scoring pour tri ensuite)
    filtered.forEach(prod => {
      prod._score = 0;
      if (p.phototype_group === 'phototype_IV_V_VI' && hasTag(prod, 'phototype_ivvi_ok')) prod._score += 3;
      if (p.gender === 'profil_homme' && hasTag(prod, 'homme_uniquement')) prod._score += 3;
      if ((p.is_pregnant || p.is_breastfeeding) && hasTag(prod, 'safe_grossesse')) prod._score += 2;
      if (p.tolerance === 'B' && hasTag(prod, 'actif_fort')) prod._score -= 1;
      if (p.tolerance === 'B' && hasTag(prod, 'parfum_present')) prod._score -= 1;
      if (p.age_group === 'age_moins_18' && hasTag(prod, 'actif_fort')) prod._score -= 2;
      if (p.existing_actives.indexOf('A') !== -1 && hasTag(prod, 'famille_actif_bha')) prod._score -= 1;
      if (p.existing_actives.indexOf('A') !== -1 && hasTag(prod, 'famille_actif_aha')) prod._score -= 1;
      if (p.existing_actives.indexOf('B') !== -1 && hasTag(prod, 'contient_vitc_pure')) prod._score -= 1;

      // RETINOL_DEBUTANT → actif_doux obligatoire
      if (p.existing_actives.indexOf('C_lt3m') !== -1 &&
          hasTag(prod, 'contient_retinol') &&
          !hasTag(prod, 'actif_doux')) prod._score -= 5;

      // Sprint stratégie homme CH.5 — Texture × type de peau sur leave-on
      // (serum/hydratant/traitement). Tags texture_* dormants côté catalogue
      // (à dériver de format_galenique par la session catalogue). Pénalité
      // forte -3 plutôt qu'exclusion dure pour ne jamais vider un pool, mais
      // suffisante pour reléguer une texture mal adaptée derrière les bonnes.
      // Mixte/normale restent neutres (pas d'opinion forte sur la galénique).
      const _isTextureStep = step === 'etape_serum' || step === 'etape_hydratant' || step === 'etape_traitement';
      if (_isTextureStep) {
        if (p.skin_type === 'profil_peau_grasse') {
          if (hasAny(prod, ['texture_fluide', 'texture_gel'])) prod._score += 2;
          if (hasAny(prod, ['texture_baume', 'texture_creme_riche'])) prod._score -= 3;
          if (hasTag(prod, 'texture_huile')) prod._score -= 1;
        } else if (p.skin_type === 'profil_peau_seche') {
          if (hasAny(prod, ['texture_creme', 'texture_creme_riche', 'texture_baume'])) prod._score += 2;
          if (hasTag(prod, 'texture_gel')) prod._score -= 3;
          if (hasTag(prod, 'texture_fluide')) prod._score -= 1;
        }
      }

      // V3.34f BUG-A — Texture × type de peau sur ÉTAPES WASH-OFF
      // (nettoyant/démaquillant). Peau sèche ou barrière fragile → pénaliser
      // fortement les textures décapantes (mousse, gel moussant) et privilégier
      // les textures douces (lait, crème, huile démaquillante). Ne s'applique
      // qu'aux peaux réellement fragiles (seche OU barrier_fragile*), sinon
      // texture neutre. Le picking ne fera JAMAIS "mousse Vit C sur peau
      // tiraillante" comme cas AUDIT du 2026-07-17.
      // Hybride : si les tags texture_* sont absents (dormants dans le catalogue),
      // on infère la texture depuis le nom produit via regex.
      const _isWashOffStep = step === 'etape_nettoyant' || step === 'etape_demaquillant';
      const _isFragile = p.skin_type === 'profil_peau_seche' ||
        p.barrier_status === 'profil_barriere_fragile' ||
        p.barrier_status === 'profil_barriere_fragile_reactive';
      if (_isWashOffStep && _isFragile) {
        const _name = (prod.name || '').toLowerCase();
        const _hasTexTag = (prod.tags || []).some(t => t.indexOf('texture_') === 0);
        // Doux
        const _tagDoux = hasAny(prod, ['texture_lait', 'texture_creme', 'texture_creme_riche', 'texture_huile', 'texture_baume', 'texture_eau_micellaire']);
        const _nameDoux = /lait|lact[eé]e?|cr[eè]me|huile|baume|eau micellaire|micellaire/i.test(_name);
        // Décapant
        const _tagDecap = hasAny(prod, ['texture_mousse', 'texture_gel', 'texture_gel_moussant']);
        const _nameDecap = /mousse|moussant|foam|gel/i.test(_name);
        if (_hasTexTag ? _tagDoux : _nameDoux) prod._score += 3;
        if (_hasTexTag ? _tagDecap : _nameDecap) prod._score -= 4;
        // Bonus double si BARRIÈRE fragile
        if (p.barrier_status === 'profil_barriere_fragile' ||
            p.barrier_status === 'profil_barriere_fragile_reactive') {
          if (_hasTexTag ? _tagDoux : _nameDoux) prod._score += 2;
          if (_hasTexTag ? _tagDecap : _nameDecap) prod._score -= 2;
        }
      }
      // Peau grasse inversé — mousse OK pour nettoyage sébum (mais pas si
      // barrière fragile en plus, cas déjà traité ci-dessus)
      if (_isWashOffStep && p.skin_type === 'profil_peau_grasse' && !_isFragile) {
        if (hasAny(prod, ['texture_mousse', 'texture_gel'])) prod._score += 1;
      }
    });

    return filtered;
  }

  function applyCompositeRules(pool, p, step, pack) {
    // Peau grasse + barrière fragile
    if (p.skin_type === 'profil_peau_grasse' &&
        (p.barrier_status === 'profil_barriere_fragile' || p.barrier_status === 'profil_barriere_fragile_reactive')) {
      if (step === 'etape_serum') {
        pool = pool.filter(pr => !(hasTag(pr, 'famille_actif_bha') && hasTag(pr, 'actif_fort')));
        pool.forEach(pr => {
          if (hasAny(pr, ['actif_niacinamide','actif_azelaique'])) pr._score = (pr._score || 0) + 2;
        });
      }
      if (step === 'etape_hydratant') {
        pool.forEach(pr => {
          if (hasTag(pr, 'type_hydratant_reparateur')) pr._score = (pr._score || 0) + 3;
        });
      }
    }

    // V3.34f BUG-B (bonus) — Toute barrière fragile/réactive (pas seulement
    // peau grasse) → boost hydratant réparateur + tags proxy céramides/panthenol.
    // Justification clinique : sur peau sèche + barrière fragile, prioriser la
    // relipidation à un hydratant traitant (ex: TXA+niacinamide sur PD-0502)
    // = évite double traitement pigment sans réparation barrière.
    if ((p.barrier_status === 'profil_barriere_fragile' ||
         p.barrier_status === 'profil_barriere_fragile_reactive') &&
        step === 'etape_hydratant') {
      pool.forEach(pr => {
        if (hasTag(pr, 'type_hydratant_reparateur')) pr._score = (pr._score || 0) + 4;
        if (hasAny(pr, ['actif_ceramides', 'actif_panthenol', 'actif_allantoine',
                        'famille_actif_reparateur'])) pr._score = (pr._score || 0) + 2;
      });
    }

    // SPF peau déshydratée
    if (p.skin_type === 'profil_peau_grasse' && p.secondary_objectives.indexOf('A') !== -1 && step === 'etape_spf') {
      pool.forEach(pr => {
        if (hasTag(pr, 'spf_hydratant'))  pr._score = (pr._score || 0) + 2;
        if (hasTag(pr, 'spf_matifiant'))  pr._score = (pr._score || 0) - 3;
      });
    }

    // Acné inflammatoire / rétentionnelle
    if (pack === 'pack_CLEAR' && step === 'etape_serum') {
      if (p.acne_type === 'A' || p.acne_type === 'C') {
        pool.forEach(pr => {
          if (hasTag(pr, 'famille_actif_bha'))       pr._score = (pr._score || 0) + 2;
          if (hasTag(pr, 'famille_actif_apaisant'))  pr._score = (pr._score || 0) + 1;
        });
      }
      if (p.acne_type === 'B' || p.acne_type === 'C') {
        pool.forEach(pr => {
          if (hasTag(pr, 'famille_actif_niacinamide')) pr._score = (pr._score || 0) + 2;
          if (hasTag(pr, 'famille_actif_bha'))         pr._score = (pr._score || 0) + 2;
        });
      }
    }

    // SPF matifiant pour peau grasse
    if (step === 'etape_spf' && p.skin_type === 'profil_peau_grasse' && p.secondary_objectives.indexOf('A') === -1) {
      pool.forEach(pr => {
        if (hasTag(pr, 'spf_matifiant')) pr._score = (pr._score || 0) + 2;
      });
    }

    return pool;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 8 — Conflits actifs (1 dominant par étape + notices alternance)
  // ────────────────────────────────────────────────────────────────────────────

  function resolveActiveConflicts(selection) {
    const conflicts = [];
    const serum = selection.products.etape_serum;
    const exfo = selection.products.etape_exfoliant;
    if (!serum) return conflicts;

    const serumRet = hasAny(serum, ['famille_actif_retinoide','contient_retinol']);
    const serumVitC = hasTag(serum, 'contient_vitc_pure');
    const exfoStrong = exfo && hasAny(exfo, ['famille_actif_bha','famille_actif_aha']) && !hasTag(exfo, 'actif_PHA');

    if (serumRet && exfoStrong) conflicts.push({ type: 'retinoid_plus_acid', notice: 'Alternance automatique : BHA le lundi/mercredi/vendredi — Rétinol le mardi/jeudi/samedi.' });
    if (serumRet && serumVitC) conflicts.push({ type: 'retinoid_plus_vitc', notice: 'VitC le matin pour l\'éclat. Rétinoïde le soir pour la régénération.' });

    return conflicts;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 9 — Cascade stock : Rang 1 → Rang 2 → Rang 3 → Joker
  // ────────────────────────────────────────────────────────────────────────────

  // Pioche produit pour une étape — retourne null si aucun compatible.
  // JAMAIS de substitution par un produit d'une autre étape (BUG 2).
  // L'étape est skippée silencieusement en aval quand null est retourné.
  //
  // Sprint 4d Phase 5 — Fallback level intelligent. Avant de tomber sur le
  // joker, on essaie de regénérer le pool depuis le catalogue pour le même
  // step et le même pack avec un niveau adjacent (cascade asymétrique).
  // La cascade est bornée par maxAllowedLevel(p) : un niveau au-dessus du
  // cap de sécurité est sauté silencieusement, JAMAIS promu. Le produit
  // retourné par fallback est marqué via _fallback_level_used.
  function pickWithCascade(step, pool, pack, catalogue, p) {
    const pick = _selectFromPool(pool, step, pack, p);
    if (pick) return pick;

    // Phase 5 — cascade asymétrique bornée par cap de sécurité.
    //   STARTER vide → essaie OPTIMAL puis PREMIUM (descente naturelle vers + fort si dispo).
    //   OPTIMAL vide → essaie PREMIUM puis STARTER (asymétrie clinique : une
    //     cliente OPTIMAL est mieux servie par PREMIUM que par STARTER en rupture).
    //   PREMIUM vide → essaie OPTIMAL puis STARTER (descente vers - fort).
    // Filtrage cap : niveaux > maxAllowedLevel(p) sautés. Si cascade vide → findJoker.
    const startLevel = computeLevel(p);
    const cap = maxAllowedLevel(p);
    const capRank = LEVEL_ORDER[cap];
    const FALLBACK_ORDER = {
      niveau_STARTER: ['niveau_OPTIMAL', 'niveau_PREMIUM'],
      niveau_OPTIMAL: ['niveau_PREMIUM', 'niveau_STARTER'],
      niveau_PREMIUM: ['niveau_OPTIMAL', 'niveau_STARTER']
    };
    const candidates = (FALLBACK_ORDER[startLevel] || [])
      .filter(lv => LEVEL_ORDER[lv] <= capRank);

    for (const fallbackLevel of candidates) {
      const fallbackPool = buildStepPool(step, pack, fallbackLevel, catalogue, p);
      const fallbackPick = _selectFromPool(fallbackPool, step, pack, p);
      if (fallbackPick) {
        // Copie shallow obligatoire — le catalogue est partagé entre runs et
        // _fallback_level_used ne doit pas persister sur l'objet produit
        // (sinon contamination entre tests / entre sessions client).
        return Object.assign({}, fallbackPick, { _fallback_level_used: fallbackLevel });
      }
    }

    // Pool vide ou tout OOS → tente le joker (même étape uniquement).
    return findJoker(step, catalogue, p);
  }

  // Sprint 4d Phase 5 — Helper interne extrait de pickWithCascade.
  // Applique tri tier+score, boosts additifs sprints 2/4a/4b/4c/4d, garde-fous
  // SPF phototype IV-VI et homme_uniquement, puis renvoie le premier produit
  // disponible. NON exposé dans module.exports (privé au module).
  //
  // Sémantique strictement identique à l'inline d'origine : pool vide → null,
  // tous OOS → null. La cascade fallback level (Phase 5) appelle ce helper
  // pour chaque pool fallback regénéré.
  function _selectFromPool(pool, step, pack, p) {
    if (!pool.length) return null;
    const sortByTierThenScore = (a, b) => {
      const ta = (a.tiers && a.tiers[pack]) || 99;
      const tb = (b.tiers && b.tiers[pack]) || 99;
      if (ta !== tb) return ta - tb;
      return (b._score || 0) - (a._score || 0);
    };
    const sorted = [...pool].sort(sortByTierThenScore);

    // ──────────────────────────────────────────────────────────────────────
    //  BOOSTS ADDITIFS — cumulent à travers les sprints. Re-tri unique en fin.
    // ──────────────────────────────────────────────────────────────────────
    let _boosted = false;

    // Sprint 2 — Slot traitement : préférence aux produits step pur (+2).
    if (step === 'etape_traitement') {
      sorted.forEach(pr => {
        if (pr.step === 'etape_traitement') pr._score = (pr._score || 0) + 2;
      });
      _boosted = true;
    }

    // AUDIT 2026-06 (corrigé 2026-08-24) — Ciblage azélaïque en SAFE : pour une
    // grossesse avec préoccupation acné, l'acide azélaïque est la référence
    // clinique (sûr, anti-inflammatoire, anti-PIH) → +4 pour qu'il prenne le
    // pas sur l'ampoule centella à tier égal. Hors acné, -1 : les apaisants
    // gardent la tête. PD-0492 a perdu son tag niveau_STARTER en parallèle
    // (actif doux, tous niveaux) pour être présent dans tous les pools SAFE.
    if (pack === 'pack_SAFE' && step === 'etape_serum') {
      const _acne = p.primary_concern === 'A';
      sorted.forEach(pr => {
        if (hasTag(pr, 'actif_azelaique')) {
          pr._score = (pr._score || 0) + (_acne ? 4 : -1);
          _boosted = true;
        }
      });
    }

    // Sprint 4a R4.2 — Péri-ménopause : peptides+2 et HA+2 sur TOUS LES PACKS en sérum.
    if (step === 'etape_serum' && p.hormonal_context === 'E') {
      sorted.forEach(pr => {
        if (hasTag(pr, 'actif_peptides')) { pr._score = (pr._score || 0) + 2; _boosted = true; }
        if (hasTag(pr, 'actif_HA'))       { pr._score = (pr._score || 0) + 2; _boosted = true; }
      });
    }

    // Sprint 4a — Boosts pack EVEN exclusifs (taches_type + hormonal × Q2_3).
    if (pack === 'pack_EVEN' && step === 'etape_serum') {
      if (p.taches_type === 'A') {
        sorted.forEach(pr => {
          if (hasTag(pr, 'actif_niacinamide'))   pr._score = (pr._score || 0) + 3;
          if (hasTag(pr, 'actif_azelaique'))     pr._score = (pr._score || 0) + 3;
          if (hasTag(pr, 'actif_niacinamide') && hasTag(pr, 'actif_zinc')) {
            pr._score = (pr._score || 0) + 2;
          }
          if (hasTag(pr, 'actif_tranexamique'))  pr._score = (pr._score || 0) + 1;
        });
        _boosted = true;
      }
      if (p.taches_type === 'B') {
        sorted.forEach(pr => {
          if (hasTag(pr, 'actif_tranexamique')) pr._score = (pr._score || 0) + 4;
          if (hasTag(pr, 'actif_thiamidol'))    pr._score = (pr._score || 0) + 3;
          if (hasTag(pr, 'actif_melasyl'))      pr._score = (pr._score || 0) + 3;
          if (hasTag(pr, 'actif_azelaique'))    pr._score = (pr._score || 0) + 2;
          if (hasTag(pr, 'actif_niacinamide'))  pr._score = (pr._score || 0) + 1;
        });
        _boosted = true;
      }
      if (p.taches_type === 'C') {
        sorted.forEach(pr => {
          if (hasTag(pr, 'actif_vitC'))         pr._score = (pr._score || 0) + 3;
          if (hasTag(pr, 'actif_niacinamide'))  pr._score = (pr._score || 0) + 1;
        });
        _boosted = true;
      }
      if (p.taches_type === 'D') {
        sorted.forEach(pr => {
          if (hasTag(pr, 'actif_niacinamide')) pr._score = (pr._score || 0) + 2;
          if (hasTag(pr, 'actif_azelaique'))   pr._score = (pr._score || 0) + 2;
        });
        _boosted = true;
      }
      // R4.1 — Q2_3=B + hormonal=D (THM) : tranex +4, cumule avec R3.2 si applicable
      if (p.primary_concern === 'B' && p.hormonal_context === 'D') {
        sorted.forEach(pr => {
          if (hasTag(pr, 'actif_tranexamique')) pr._score = (pr._score || 0) + 4;
        });
        _boosted = true;
      }
      // R4.3 — Q2_3=B + hormonal=A (pilule) : tranex +3
      if (p.primary_concern === 'B' && p.hormonal_context === 'A') {
        sorted.forEach(pr => {
          if (hasTag(pr, 'actif_tranexamique')) pr._score = (pr._score || 0) + 3;
        });
        _boosted = true;
      }
    }
    // R3.3 sur exfoliant : AHA modérés boostés (taches=C, pack EVEN)
    if (pack === 'pack_EVEN' && step === 'etape_exfoliant' && p.taches_type === 'C') {
      sorted.forEach(pr => {
        if (hasTag(pr, 'famille_actif_aha') && !hasTag(pr, 'actif_fort')) {
          pr._score = (pr._score || 0) + 2;
          _boosted = true;
        }
      });
    }

    // Sprint 4b R3.3 — Diabète (medicaments='C') : boost +2 réparateurs (catégoriel, hasAny).
    if (hasMedicament(p, 'C')) {
      sorted.forEach(pr => {
        if (hasAny(pr, ['actif_centella', 'actif_panthenol', 'actif_ceramides', 'famille_actif_reparateur'])) {
          pr._score = (pr._score || 0) + 2;
          _boosted = true;
        }
      });
    }

    // Sprint 4b R3.4 — Thyroïdien (medicaments='D') : boost +2 hydratants/apaisants.
    if (hasMedicament(p, 'D')) {
      sorted.forEach(pr => {
        if (hasAny(pr, ['actif_HA', 'actif_squalane', 'actif_ceramides', 'actif_panthenol', 'famille_actif_apaisant'])) {
          pr._score = (pr._score || 0) + 2;
          _boosted = true;
        }
      });
    }

    // Sprint 4d Phase 4 R3 — Rasage barbe entretenue (Q7_9='B') : boost +1 sur
    // actif_centella OU actif_panthenol, TOUS STEPS confondus (apaisants
    // utiles à toutes les phases de la routine sur peau d'entretien barbe).
    // Volontairement hors de tout if (step === ...) : caractère transverse
    // qui doit s'appliquer en nettoyant, sérum, hydratant, etc.
    if (p.rasage === 'B') {
      sorted.forEach(pr => {
        if (hasAny(pr, ['actif_centella', 'actif_panthenol'])) {
          pr._score = (pr._score || 0) + 1;
          _boosted = true;
        }
      });
    }

    // Sprint 4c R2.1 — Tabac quotidien : antioxydants +3 + SPF_50_plus +2 (dormant).
    if (p.tabac === 'A') {
      sorted.forEach(pr => {
        if (hasAny(pr, ['actif_vitC', 'actif_vitE', 'actif_polyphenols', 'actif_ferulique', 'actif_resveratrol', 'famille_actif_antioxydant'])) {
          pr._score = (pr._score || 0) + 3;
          _boosted = true;
        }
        // Tag SPF_50_plus dormant : aucun produit catalogue ne le porte aujourd'hui.
        // Boost actif uniquement quand la session catalogue appliquera le tag.
        if (hasTag(pr, 'SPF_50_plus')) {
          pr._score = (pr._score || 0) + 2;
          _boosted = true;
        }
      });
    }
    // Sprint 4c R2.2 — Tabac occasionnel : antioxydants +1, pas de boost SPF.
    if (p.tabac === 'B') {
      sorted.forEach(pr => {
        if (hasAny(pr, ['actif_vitC', 'actif_vitE', 'actif_polyphenols', 'actif_ferulique', 'actif_resveratrol', 'famille_actif_antioxydant'])) {
          pr._score = (pr._score || 0) + 1;
          _boosted = true;
        }
      });
    }

    // Sprint 4c R5.1 — Acné femme adulte hormonale (pilule + Q2_3=A + pack CLEAR + sérum).
    if (pack === 'pack_CLEAR' && step === 'etape_serum'
        && p.hormonal_context === 'A' && p.primary_concern === 'A') {
      sorted.forEach(pr => {
        if (hasTag(pr, 'actif_zinc'))        pr._score = (pr._score || 0) + 2;
        if (hasTag(pr, 'actif_azelaique'))   pr._score = (pr._score || 0) + 2;
        if (hasTag(pr, 'actif_niacinamide')) pr._score = (pr._score || 0) + 1;
      });
      _boosted = true;
    }

    if (_boosted) sorted.sort(sortByTierThenScore);

    if (step === 'etape_spf' && p.phototype_group === 'phototype_IV_V_VI') {
      const mela = sorted.find(pr => pr.id === 'PD-0377' && isAvailable(pr));
      if (mela) return mela;
    }
    // Stratégie homme — préférence DOUCE (changement 1) : on a retiré le
    // court-circuit qui renvoyait systématiquement le 1er produit
    // homme_uniquement avant le tri normal. Le genre homme conserve ses deux
    // autres leviers : l'exclusion femme (applyInciFilters) et le boost +3
    // (appliqué plus haut dans _selectFromPool). Le pick suit désormais le tri
    // normal tier d'abord puis _score : un produit homme tier 2 + boost ne
    // passe plus devant un unisexe tier 1 ; il ne gagne qu'à tier égal/meilleur.
    const pick = sorted.find(pr => isAvailable(pr));
    return pick || null;
  }

  // Retourne le joker DE L'ÉTAPE DEMANDÉE, ou null si aucun joker n'existe
  // pour cette étape (cas de etape_exfoliant, etape_zone_specifique sans match).
  // Les jokers sont garantis en stock par définition (cahier des charges §4 étape 9),
  // donc on ignore `available` pour leur lookup — c'est le dernier recours.
  // V3.34f — findJoker 100% dynamique. Étapes :
  // (1) tente le hint hardcodé (PD-XXXX) s'il existe encore dans le live ;
  // (2) sinon pioche dans le catalogue live tous les produits du step, filtre
  //     par disponibilité + safety grossesse le cas échéant, ordonne par
  //     priorité clinique (safe_grossesse > apaisant > réparateur > premier)
  //     et retourne le meilleur candidat vivant.
  // Résultat : plus aucune étape skippée à cause d'un ID fantôme.
  function findJoker(step, catalogue, p) {
    let hintId = JOKER_HINTS[step];
    if (step === 'etape_spf' && p && p.phototype_group === 'phototype_IV_V_VI') {
      hintId = JOKER_HINTS.etape_spf_ivvi;
    }
    // AUDIT 2026-06 (corrigé 2026-08-24) — le joker passe désormais les filtres
    // INCI comme n'importe quel produit : le hint hardcodé était retourné sans
    // aucun contrôle, et servait p.ex. un nettoyant parfumé à une femme
    // enceinte quand le pool SAFE était en rupture (parfum_present est dans
    // PREGNANCY_EXCLUDES). Un joker inéligible → fallback dynamique, lui aussi
    // filtré. Pool vide après filtres → null (étape skippée, jamais un produit
    // contre-indiqué).
    const inciOk = (products) => applyInciFilters(products, p || {}, step, null);
    // (1) Hint hardcodé — si vivant dans le catalogue live ET conforme INCI
    if (hintId) {
      const hint = catalogue.find(pr => pr.id === hintId && isAvailable(pr));
      if (hint && inciOk([hint]).length) return hint;
    }
    // (2) Fallback dynamique — pioche le produit le plus safe du step
    const stepPool = inciOk(catalogue.filter(pr => pr.step === step && isAvailable(pr)));
    if (!stepPool.length) return null;
    const isPregnantOrBreastfeeding = p && (p.is_pregnant || p.is_breastfeeding);
    const scoreForSafety = (pr) => {
      let score = 0;
      const tags = pr.tags || [];
      // Grossesse : safe_grossesse indispensable si pregnancy/breastfeeding
      if (isPregnantOrBreastfeeding) {
        if (tags.indexOf('contre_indique_grossesse') !== -1) return -999;
        if (tags.indexOf('safe_grossesse') !== -1) score += 10;
        else score -= 5;
      }
      // Sans parfum privilégié (universel)
      if (tags.indexOf('fragrance_free') !== -1) score += 3;
      if (tags.indexOf('parfum_present') !== -1) score -= 2;
      // Familles apaisantes/réparatrices en priorité (jokers = safe)
      if (hasTag(pr, 'famille_actif_apaisant')) score += 3;
      if (hasTag(pr, 'famille_actif_reparateur')) score += 2;
      // Éviter les actifs forts en fallback (peut agresser)
      if (hasTag(pr, 'actif_fort')) score -= 5;
      // Niveau STARTER privilégié (le plus accessible)
      if (pr.level === 'niveau_STARTER') score += 2;
      return score;
    };
    const sorted = stepPool
      .map(pr => ({ pr, s: scoreForSafety(pr) }))
      .filter(x => x.s > -100)
      .sort((a, b) => b.s - a.s);
    return sorted.length ? sorted[0].pr : null;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 10 — Sélection compléments (scoring +3/+2/-2/-3 ; max 2)
  // ────────────────────────────────────────────────────────────────────────────

  // CORRECTION 3 — Rôle attribué par RANG (essentiel/recommande/optionnel),
  // jamais par ratio. Le 1er sélectionné est toujours Essentiel.
  function getComplementRole(rank) {
    if (rank === 0) return 'essentiel';
    if (rank === 1) return 'recommande';
    if (rank === 2) return 'optionnel';
    return null;
  }

  // CORRECTION 5 — dictionnaire notices élargi.
  // Priorité : actif+pack > actif+mode_vie > actif seul > default.
  // La mention grossesse/allaitement s'ajoute TOUJOURS en supplément.
  const COMPLEMENT_NOTICES = {
    'actif_omega3_pack_REPAIR': "Soutient la reconstruction de la barrière cutanée de l'intérieur.",
    'actif_omega3_pack_CALM':   "Contribue à réduire l'inflammation cutanée systémique.",
    'actif_omega3_pack_CLEAR':  "Action anti-inflammatoire systémique, complément naturel du traitement topique acné.",
    'actif_omega3_pack_SAFE':   "Apport en DHA essentiel pendant la grossesse.",
    'actif_zinc_pack_CLEAR':    "Contribue à réguler la séborrhée et réduire l'inflammation cutanée.",
    'actif_zinc_pack_EVEN':     "Réduit l'inflammation locale responsable des taches post-acné.",
    'actif_collagene_pack_LIFT':"Stimule la synthèse de collagène cutané pour améliorer la fermeté.",
    'actif_collagene_pack_REPAIR':"Soutient la reconstruction du tissu cutané fragilisé.",
    'actif_collagene_pack_EVEN':"Améliore l'élasticité cutanée et unifie le grain de peau.",
    'actif_vitC_pack_EVEN':     "Potentialise l'action des actifs dépigmentants topiques.",
    'actif_vitC_pack_GLOW':     "Antioxydant puissant, éclat et protection cellulaire de l'intérieur.",
    'actif_vitC_pack_LIFT':     "Cofacteur essentiel de la synthèse de collagène.",
    'actif_ha_pack_REPAIR':     "Hydratation profonde et soutien de l'élasticité cutanée fragilisée.",
    'actif_ha_pack_LIFT':       "Soutient l'hydratation profonde et l'élasticité cutanée.",
    'actif_ha_pack_CALM':       "Hydratation intense sans actif irritant, idéal peau sensible.",
    'actif_magnesium_mode_vie_stress':  "Contribue à réduire la fatigue et soutient l'équilibre nerveux.",
    'actif_magnesium_mode_vie_sommeil': "Favorise la relaxation musculaire et la qualité du sommeil.",
    'actif_magnesium_pack_SAFE': "Apport minéral de soutien pendant la grossesse.",
    'actif_vitD_pack_SAFE':     "Vitamine D3 recommandée systématiquement pendant la grossesse en France.",
    'actif_vitD_pack_REPAIR':   "Soutient la fonction immunitaire et la régénération cutanée.",
    'actif_polypodium_pack_EVEN': "Photoprotection interne, renforce la résistance de la peau aux UV, indispensable sur phototype IV-VI.",
    'actif_polypodium_pack_GLOW': "Antioxydant solaire interne, protège l'éclat et prévient le vieillissement photo-induit.",
    'actif_oxyprolane_pack_EVEN': "Soutient l'éclat et l'uniformité du teint de l'intérieur.",
    'actif_oxyprolane_pack_GLOW': "Stimule la synthèse de collagène et l'hydratation profonde.",
    'actif_oxyprolane_pack_LIFT': "Soutient la fermeté et l'éclat cutané par voie nutritionnelle.",
    'actif_glutathion_pack_EVEN': "Dépigmentant inside-out, référence anti-taches sur phototypes IV-VI.",
    'actif_ashwagandha_mode_vie_stress': "Adaptogène, aide l'organisme à mieux gérer le stress chronique.",
    'default': "Sélectionné par notre pharmacienne pour compléter votre Protocole."
  };
  const COMPLEMENT_PREGNANCY_NOTICE = "Demandez l'avis de votre médecin ou sage-femme avant de débuter.";
  const COMPLEMENT_DEFAULT_NOTICE  = COMPLEMENT_NOTICES.default;

  // Map produit → clé actif principale pour notice (ex: Vitamine C → actif_vitC).
  // Couvre les noms courants même si le tag exact n'est pas standardisé dans le catalogue.
  const COMPLEMENT_ACTIF_KEY_BY_NAME = [
    [/oméga\s*-?\s*3|omega/i, 'actif_omega3'],
    [/collagèn|collagen/i,    'actif_collagene'],
    [/zinc/i,                 'actif_zinc'],
    [/magnésium|magnesium/i,  'actif_magnesium'],
    [/vitamine\s*c|vit[aé]?c|ascorbic/i, 'actif_vitC'],
    [/vitamine\s*d|vit\s*d|cholécalciférol/i, 'actif_vitD'],
    [/hyaluron/i,             'actif_ha'],
    [/polypodium|leucotomos|fougère/i, 'actif_polypodium'],
    [/oxyprolane/i,           'actif_oxyprolane'],
    [/glutathion/i,           'actif_glutathion'],
    [/ashwagandha/i,          'actif_ashwagandha'],
    [/heliocare/i,            'actif_polypodium']
  ];
  function pickComplementActif(product) {
    const direct = ['actif_omega3','actif_zinc','actif_collagene','actif_vitC','actif_vitD',
      'actif_ha','actif_magnesium','actif_ashwagandha','actif_polypodium','actif_oxyprolane','actif_glutathion'];
    const tag = (product.actifs || []).find(a => direct.indexOf(a) !== -1);
    if (tag) return tag;
    const name = (product.brand ? product.brand + ' ' : '') + (product.name || '');
    for (const [re, key] of COMPLEMENT_ACTIF_KEY_BY_NAME) {
      if (re.test(name)) return key;
    }
    return null;
  }

  function complementNoticeFor(product, pack, lifestyle) {
    const a = pickComplementActif(product);
    if (a) {
      const key1 = `${a}_${pack}`;
      if (COMPLEMENT_NOTICES[key1]) return COMPLEMENT_NOTICES[key1];
      for (const mv of (lifestyle || [])) {
        const key2 = `${a}_${mv}`;
        if (COMPLEMENT_NOTICES[key2]) return COMPLEMENT_NOTICES[key2];
      }
      if (COMPLEMENT_NOTICES[a]) return COMPLEMENT_NOTICES[a];
    }
    return COMPLEMENT_DEFAULT_NOTICE;
  }

  // Renvoie la famille actif "principale" (1er élément normalisé) — utilisé pour
  // dedup (CORRECTION 4) et exposé sur l'output (test 72).
  function primaryFamilleActif(product) {
    const fa = (product.familles_actif && product.familles_actif[0]) || null;
    if (fa) return fa.replace('famille_actif_', '');
    if (product.famille_complement) return product.famille_complement.replace('complement_tag_', '');
    return null;
  }

  function selectComplements(p, pack, catalogue) {
    let pool = catalogue.filter(pr => {
      if (pr.type !== 'complement') return false;
      if (!isAvailable(pr)) return false;
      // Sprint stratégie homme CH.4 — Exclusion dure même côté compléments :
      // un format voyage ou échantillon GWP n'a rien à faire dans une cure.
      if (hasTag(pr, 'format_voyage') || hasTag(pr, 'gwp_sample') || hasTag(pr, 'addon_bebe')) return false;
      if ((p.is_pregnant || p.is_breastfeeding) && hasTag(pr, 'contre_indique_grossesse')) return false;
      if ((p.is_pregnant || p.is_breastfeeding) && hasTag(pr, 'complement_tag_solaire')) return false;
      if ((p.is_pregnant || p.is_breastfeeding) && hasTag(pr, 'complement_tag_hormonal')) return false;
      return true;
    });
    if (!pool.length) return [];

    pool.forEach(pr => {
      let s = 0;
      if (pr.packs && pr.packs.indexOf(pack) !== -1) s += 3;
      if (p.secondary_pack && pr.packs && pr.packs.indexOf(p.secondary_pack) !== -1) s += 2;
      // V3.34f BUG-C — Boost lifestyle. +4 insuffisant face à pack (+3)+HERO
      // (+3)+secondary (+2) = 8. Un complément mode_vie hors pack faisait
      // seulement 4 vs concurrents 6-8 → perdait. Boost passé à +7 pour
      // garantir couverture MDV explicite (stress, alimentation) même hors
      // pack. Reste composable avec pickedBrands + pickedThemes.
      p.lifestyle.forEach(tag => {
        if (hasTag(pr, complementTagForLifestyle(tag))) s += 7;
      });
      // Sprint 4d Phase 2 — Boost dépigmentant étendu de phototype_IV_V_VI à
      // phototype_at_risk_pih. Justification : c'est un boost (synergie inside-out),
      // pas un filtre restrictif, donc aucun conflit avec R2.5. Phototype III avec
      // antécédent PIH personnel ou flou bénéficie légitimement de compléments
      // dépigmentants en synergie avec sa routine topique, sans rien lui retirer.
      if (p.phototype_at_risk_pih && hasTag(pr, 'complement_tag_depigmentant')) s += 2;
      if (p.is_pregnant && hasTag(pr, 'conditionnel_grossesse')) s -= 2;
      // Sprint 4b R3.5 — Antibiothérapie longue durée : +5 sur complement_probiotiques (tag dormant).
      if (hasMedicament(p, 'E') && hasTag(pr, 'complement_probiotiques')) s += 5;
      // Sprint 4c — Exclusion des compléments déjà pris (-9999 APRÈS le boost antibio
      // pour que l'exclusion prime). Tags dormants jusqu'à session catalogue.
      if (Array.isArray(p.complements_pris) && p.complements_pris.length) {
        for (const code of p.complements_pris) {
          const tags = COMPLEMENTS_PRIS_MAP[code];
          if (tags && tags.some(t => hasTag(pr, t))) { s -= 9999; break; }
        }
      }
      // HANDOFF audit — Boost « le plus adapté » : la famille hero de la
      // préoccupation remonte en tête (ex. LIFT→collagène, EVEN→dépigmentant,
      // CLEAR→purifiant). Complète sans écraser les autres boosts.
      var HERO_COMPL = ({
        pack_LIFT:   ['complement_tag_collagene'],
        pack_REPAIR: ['complement_tag_anti_inflammatoire', 'complement_tag_barriere'],
        pack_EVEN:   ['complement_tag_depigmentant'],
        pack_GLOW:   ['complement_tag_eclat', 'complement_tag_depigmentant'],
        pack_CLEAR:  ['complement_tag_purifiant', 'complement_probiotiques', 'complement_tag_microbiote'],
        pack_CALM:   ['complement_tag_apaisant', 'complement_tag_anti_inflammatoire']
      })[pack] || [];
      if (HERO_COMPL.some(t => hasTag(pr, t))) s += 3;
      pr._cscore = s;
    });

    pool.sort((a, b) => (b._cscore || 0) - (a._cscore || 0));

    // CORRECTION 4 + HANDOFF audit — anti-doublon strict famille actif AVANT
    // slice top 3, ÉTENDU avec diversité de marque (pas 3× la même) + dédup
    // thème (pigment=dépigmentant seul, collagène incl. vegan → pas de triplon).
    // Quintuple verrou : ID via COMPLEMENT_DUP_RULES, primary famille, tag
    // famille_complement, brand, thème complémentarité.
    const picked = [];
    const pickedFamilles = new Set();
    const pickedComplementTags = new Set();
    const pickedDupGroups = new Set();
    const pickedBrands = new Set();          // DIVERSITÉ DE MARQUE (pas 3× la même)
    const pickedThemes = new Set();          // pigment/collagène : 1 seul → complémentarité
    const deferred = [];                     // candidats valides mais marque déjà prise

    const STRICT_FC = ['complement_tag_collagene','complement_tag_anti_inflammatoire',
      'complement_tag_purifiant','complement_tag_solaire','complement_tag_depigmentant',
      'complement_tag_hormonal','complement_tag_stress'];

    const tryPick = (pr, allowRepeatBrand) => {
      if (picked.length >= 3) return false;
      if ((pr._cscore || 0) <= 0) return false;
      for (const family in COMPLEMENT_DUP_RULES) {
        if (COMPLEMENT_DUP_RULES[family].indexOf(pr.id) !== -1 && pickedDupGroups.has(family)) return false;
      }
      const fa = primaryFamilleActif(pr);
      if (fa && pickedFamilles.has(fa)) return false;
      const fc = pr.famille_complement;
      if (fc && STRICT_FC.indexOf(fc) !== -1 && pickedComplementTags.has(fc)) return false;
      // Dédup THÈME complémentarité : pas 2× pigment/éclat ni 2× collagène
      // (évite « 3 anti-taches » ou « 2 collagènes » → on varie les bénéfices).
      const theme = hasTag(pr,'complement_tag_depigmentant') ? 'pigment'
        : (hasTag(pr,'complement_tag_collagene') || hasTag(pr,'famille_actif_collagene') || hasTag(pr,'famille_actif_collagene_vegan')) ? 'collagene' : null;
      if (theme && pickedThemes.has(theme)) return false;
      if (!allowRepeatBrand && pr.brand && pickedBrands.has(pr.brand)) return false;

      pr._complement_role = getComplementRole(picked.length);
      pr._complement_category = (pr.packs && pr.packs.indexOf(pack) !== -1) ? 'cure_de_fond' : 'mode_de_vie';
      pr._complement_notice = complementNoticeFor(pr, pack, p.lifestyle);
      pr._famille_actif_key = fa;
      picked.push(pr);
      if (fa) pickedFamilles.add(fa);
      if (fc) pickedComplementTags.add(fc);
      if (pr.brand) pickedBrands.add(pr.brand);
      { const th = hasTag(pr,'complement_tag_depigmentant')?'pigment':((hasTag(pr,'complement_tag_collagene')||hasTag(pr,'famille_actif_collagene')||hasTag(pr,'famille_actif_collagene_vegan'))?'collagene':null); if(th) pickedThemes.add(th); }
      for (const family in COMPLEMENT_DUP_RULES) {
        if (COMPLEMENT_DUP_RULES[family].indexOf(pr.id) !== -1) pickedDupGroups.add(family);
      }
      return true;
    };

    // Passage 1 — une marque différente par slot (le pool est déjà trié par score,
    // donc on garde « le plus adapté » tout en variant les marques : Oxyprolane,
    // Argalys, Bio-Recherche… remontent au lieu du monopole D-Lab).
    for (const pr of pool) {
      if (picked.length >= 3) break;
      if ((pr._cscore || 0) <= 0) break;
      if (pr.brand && pickedBrands.has(pr.brand)) { deferred.push(pr); continue; }
      tryPick(pr, false);
    }
    // Passage 2 — compléter avec les différés (même marque autorisée) si < 3.
    for (const pr of deferred) {
      if (picked.length >= 3) break;
      tryPick(pr, true);
    }
    return picked;
  }

  function complementTagForLifestyle(tag) {
    return {
      mode_vie_sommeil:     'complement_tag_stress',
      mode_vie_stress:      'complement_tag_stress',
      mode_vie_alimentation:'complement_tag_micronutriment',
      mode_vie_sport:       'complement_tag_anti_inflammatoire',
      mode_vie_soleil:      'complement_tag_solaire'
    }[tag] || tag;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 11 — Progressivité rétinoïde
  // ────────────────────────────────────────────────────────────────────────────

  function retinoidProgression(p, selection) {
    const products = Object.values(selection.products).filter(Boolean);
    const hasRetinoid = products.some(pr => hasAny(pr, ['famille_actif_retinoide','contient_retinol']));
    if (!hasRetinoid) return null;
    if (p.age_group === 'age_moins_18') return null; // jamais (déjà filtré étape 6)

    if (p.existing_actives.indexOf('C_lt3m') !== -1 || (p.existing_actives.indexOf('C_lt3m') === -1 && p.existing_actives.indexOf('C_gte3m') === -1)) {
      return { profile: 'RETINOL_DEBUTANT',
        protocol: 'Semaines 1–4 : 1 soir par semaine. Semaines 5–8 : 2 soirs par semaine. À partir de la semaine 9 : 1 soir sur 2, puis tous les soirs si toléré.' };
    }
    return { profile: 'RETINOL_HABITUE',
      protocol: '1 soir sur 2 à adapter vers usage quotidien si votre peau le tolère.' };
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 12 — Notices dynamiques produit
  // ────────────────────────────────────────────────────────────────────────────

  // Notices produit — UNIQUEMENT des alertes cliniques spécifiques au produit.
  // JAMAIS de notice générique "usage recommandé" (BUG 4).
  // Notice SAFE : jamais par produit, toujours en header de section (BUG 4).
  function productNotices(product, p, selection, pack) {
    if (!product) return [];
    const out = [];

    if (hasAny(product, ['contient_retinol','actif_retinal','actif_retinol','famille_actif_retinoide'])) {
      out.push('Usage soir uniquement. Débuter 1×/semaine et augmenter progressivement. Contre-indiqué pendant la grossesse et l\'allaitement.');
    }
    if (hasTag(product, 'contient_acide_fort') && p.uv_exposure === 'forte') {
      out.push('Usage soir strict. Appliquer votre SPF chaque matin sans exception.');
    }
    if (hasTag(product, 'famille_actif_bha') && p.sensitivities.indexOf('A') !== -1) {
      out.push('Même famille chimique que l\'aspirine. Patch test recommandé avant utilisation.');
    }
    if (product.step === 'etape_spf' && p.phototype_group === 'phototype_IV_V_VI') {
      out.push('Le SPF est un actif traitement à part entière sur votre phototype. Indispensable chaque matin pour protéger les résultats obtenus.');
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  STEP 13 — Construction output final (7 blocks)
  // ────────────────────────────────────────────────────────────────────────────

  function run(answers, catalogue) {
    if (!Array.isArray(catalogue)) throw new Error('catalogue must be an array');

    const p = collectInputs(answers);
    const level = computeLevel(p);
    const packs = determinePack(p);
    p.secondary_pack = packs.secondary_pack; // HANDOFF audit — sans ça tout le mécanisme secondaire ET le boost compléments sont inertes
    const immediateAlerts = computeImmediateAlerts(p);

    const selection = { products: {}, pack: packs.face_pack, level: level, body_products: [] };

    // STEP 5/6/7/9 — Sélection étape par étape
    const steps = buildActiveSteps(p, packs, level);

    // V3.34f BUG-B — Track HERO actives déjà servis dans les étapes leave-on
    // précédentes pour éviter les vrais doublons cliniques (rétinoïde ×2 =
    // surdosage, acide fort ×2 = irritation, vitC pure ×2 = redondance). PAS
    // niacinamide/HA/céramides/panthenol (coexistence légitime).
    const LEAVE_ON_HEROES = [
      'actif_retinal', 'actif_retinol', 'famille_actif_retinoide', 'contient_retinol',
      'contient_acide_fort', 'actif_fort',
      'contient_vitc_pure'
    ];
    const _pickedHeroes = new Set();
    const _leaveOn = new Set(['etape_serum', 'etape_traitement', 'etape_hydratant', 'etape_exfoliant', 'etape_toner']);

    for (const step of steps) {
      let pool = buildStepPool(step, packs.face_pack, level, catalogue, p);
      // Sprint 2 — Déduplication sérum/traitement : un hybride éligible aux deux
      // slots (via compatible_steps) ne doit pas être pioché aux deux.
      if (step === 'etape_traitement' && selection.products.etape_serum) {
        const serumId = selection.products.etape_serum.id;
        pool = pool.filter(pr => pr.id !== serumId);
      }
      // V3.34f BUG-B — Anti-doublon HERO leave-on : si un HERO est déjà présent
      // dans une étape leave-on précédente, pénaliser -5 les produits qui le
      // portent pour l'étape courante. Le picking peut quand même y retomber si
      // aucun autre candidat viable ; sinon on préfère la diversification.
      if (_leaveOn.has(step) && _pickedHeroes.size > 0) {
        pool.forEach(pr => {
          for (const h of _pickedHeroes) {
            if (hasTag(pr, h)) { pr._score = (pr._score || 0) - 5; break; }
          }
        });
      }
      const pick = pickWithCascade(step, pool, packs.face_pack, catalogue, p);
      // BUG 2 — JAMAIS de substitution depuis une autre étape.
      // Si pick est null → l'étape est skippée silencieusement (absente de l'output).
      if (pick) {
        // V3.34f UX1 — Rôle UX pour badges couleur essentiel/recommande/confort
        // Essentiels = trio minimum vital (nettoyant/hydratant/SPF)
        // Recommandés = fort impact selon profil (sérum du pack + démaquillant si maquillage)
        // Confort = enrichissement quand rythme installé
        pick._role_ux = ({
          etape_nettoyant: 'essentiel',
          etape_hydratant: 'essentiel',
          etape_spf: 'essentiel',
          etape_serum: 'recommande',
          etape_demaquillant: 'recommande',
          etape_toner: 'confort',
          etape_exfoliant: 'confort',
          etape_traitement: 'confort',
          etape_zone_specifique: 'confort'
        })[step] || 'confort';
        selection.products[step] = pick;
        // Enregistrer les HERO du produit pické pour l'anti-doublon des étapes suivantes
        if (_leaveOn.has(step)) {
          LEAVE_ON_HEROES.forEach(h => { if (hasTag(pick, h)) _pickedHeroes.add(h); });
        }
      }
    }

    // HANDOFF audit — Objectif secondaire VISIBLE. Rendre le secondary_pack
    // détectable dans la routine via UN élément support portant l'actif
    // SIGNATURE (GLOW→vitC, EVEN→TXA/arbutine/azélaïque, LIFT→peptides/rétinoïde),
    // si absent ailleurs. Leave-on prioritaire (hydratant>toner>nettoyant),
    // jamais un nettoyant moussant sur peau sèche/fragile, INCI-filtré. Ne
    // touche NI au sérum NI à la structure.
    if (p.secondary_pack) {
      const SIG = ({
        pack_GLOW: ['actif_vitC', 'contient_vitc_pure'],
        pack_EVEN: ['actif_tranexamique', 'actif_arbutine', 'actif_azelaique', 'actif_vitC'],
        pack_LIFT: ['actif_peptides', 'actif_retinal', 'actif_retinol', 'actif_bakuchiol']
      })[p.secondary_pack] || [];
      const carriesSig = pr => SIG.some(t => hasTag(pr, t));
      const already = SIG.length && Object.keys(selection.products)
        .some(st => selection.products[st] && carriesSig(selection.products[st]));
      const dryOrFragile = p.skin_type === 'profil_peau_seche'
        || p.barrier_status === 'profil_barriere_fragile'
        || p.barrier_status === 'profil_barriere_fragile_reactive';
      const isFoaming = pr => /mousse|moussant|foam/i.test(pr.name || '');
      if (SIG.length && !already) {
        const SUPPORT = ['etape_hydratant', 'etape_toner', 'etape_nettoyant'];
        let repStep = null, repProd = null;
        for (const st of SUPPORT) {
          if (!selection.products[st]) continue;
          let cands = catalogue.filter(pr => pr.step === st
            && pr.packs && pr.packs.indexOf(p.secondary_pack) !== -1
            && isAvailable(pr)
            && !hasTag(pr, 'format_voyage') && !hasTag(pr, 'gwp_sample') && !hasTag(pr, 'addon_bebe')
            && carriesSig(pr) && pr.id !== selection.products[st].id
            && !(st === 'etape_nettoyant' && dryOrFragile && isFoaming(pr)));
          cands = applyInciFilters(cands, p, st, p.secondary_pack);
          if (cands.length) {
            cands.sort((a, b) => ((a.tiers && a.tiers[p.secondary_pack]) || 99)
                               - ((b.tiers && b.tiers[p.secondary_pack]) || 99));
            repStep = st; repProd = cands[0];
            break;
          }
        }
        if (repProd) selection.products[repStep] = repProd;
      }
    }

    // Contour yeux (Q7.2=G)
    if (p.secondary_objectives.indexOf('G') !== -1) {
      const eyePool = catalogue.filter(pr => pr.step === 'etape_zone_specifique' && isAvailable(pr))
        .filter(pr => {
          // filtre par eye_concern
          if (p.eye_concern === 'A') return hasTag(pr, 'eye_concern_CAFEINE') || hasTag(pr, 'actif_cafeine');
          if (p.eye_concern === 'B') return (p.is_pregnant || p.is_breastfeeding || p.age_group === 'age_moins_18')
            ? hasTag(pr, 'zone_yeux_peptides')
            : hasAny(pr, ['zone_yeux_peptides','zone_yeux_retinoide']);
          if (p.eye_concern === 'C') return hasAny(pr, ['famille_actif_apaisant','famille_actif_reparateur']);
          return true;
        });
      // Filtres de sécurité génériques
      const eyeFiltered = applyInciFilters(eyePool, p, 'etape_zone_specifique', packs.face_pack);
      const eyePick = eyeFiltered.length ? eyeFiltered[0] : findJoker('etape_zone_specifique', catalogue, p);
      if (eyePick) selection.products.etape_zone_specifique = eyePick;
    }

    // STRETCH — produits corps séparés
    if (packs.body_pack === 'pack_STRETCH') {
      // AUDIT 2026-06-14 — body_products passe désormais les filtres INCI/grossesse
      // (auparavant non filtrés → lait corps parfumé PD-0381 servi en grossesse).
      let bodyCandidates = catalogue.filter(pr =>
        isAvailable(pr) &&
        pr.packs && pr.packs.indexOf('pack_STRETCH') !== -1 &&
        ['addon_corps','etape_soin_corps'].indexOf(pr.step) !== -1 &&
        (!pr.level || pr.level === level)
      );
      bodyCandidates = applyInciFilters(bodyCandidates, p, 'addon_corps', 'pack_STRETCH');
      selection.body_products = bodyCandidates.slice(0, 3);
    }

    // Stratégie homme — bloc BARBE (changement 2). Q7_9='B' (porte une barbe) :
    // add-on soins barbe (tag soin_barbe, dormant côté catalogue), top 2 triés
    // par tiers.barbe. Pas de joker (si vide → bloc absent). Le tag
    // homme_uniquement sur ces produits exclut déjà les femmes en amont.
    let barbe_products = [];
    if (p.rasage === 'B') {
      barbe_products = catalogue
        .filter(pr => isAvailable(pr) && hasTag(pr, 'soin_barbe'))
        .sort((a, b) => ((a.tiers && a.tiers.barbe) || 99) - ((b.tiers && b.tiers.barbe) || 99))
        .slice(0, 2);
    }

    // Stratégie homme — bloc RASAGE (changement 3). Q7_9='A' (rase
    // régulièrement) : add-on soins rasage (tag soin_rasage, dormant), top 2
    // triés par tiers.rasage. PD-0520 (après-rasage) peut apparaître ici ET
    // comme hydratant de routine CALM — double usage volontaire, aucun
    // traitement spécial.
    let rasage_products = [];
    if (p.rasage === 'A') {
      rasage_products = catalogue
        .filter(pr => isAvailable(pr) && hasTag(pr, 'soin_rasage'))
        .sort((a, b) => ((a.tiers && a.tiers.rasage) || 99) - ((b.tiers && b.tiers.rasage) || 99))
        .slice(0, 2);
    }

    // STEP 8 — conflits actifs
    const conflicts = resolveActiveConflicts(selection);
    // STEP 10 — compléments
    const complements = selectComplements(p, packs.face_pack, catalogue);
    // STEP 11 — progressivité rétinoïde
    const retProto = retinoidProgression(p, selection);

    // Sprint 4d Phase 5 — Notice fallback_level. Si AU MOINS UN produit
    // sélectionné porte _fallback_level_used (positionné par pickWithCascade
    // quand le niveau exact est en rupture), on injecte UNE SEULE notice
    // dans clinical_notices, peu importe le nombre d'étapes concernées.
    const clinicalNotices = buildClinicalNotices(p, packs, level, answers);
    const hasFallbackUsed = Object.values(selection.products).some(pr => pr && pr._fallback_level_used);
    if (hasFallbackUsed) {
      clinicalNotices.push({
        type: 'fallback_level',
        text: "Pour cette étape, le niveau exact correspondant à votre profil n'était pas disponible. Nous avons sélectionné une alternative équivalente du même pack."
      });
    }

    // STEP 13 — output final
    const routineLabel = (ROUTINE_DISPLAY[packs.face_pack] || {}).name || 'Routine personnalisée';
    const routineDescriptor = (ROUTINE_DISPLAY[packs.face_pack] || {}).descriptor || '';
    const routineColors = PACK_COLORS[packs.face_pack] || PACK_COLORS.pack_CLEAR;
    return {
      profile: {
        skin_type:       p.skin_type,
        barrier_status:  p.barrier_status,
        primary_pack:    packs.primary_pack,
        secondary_pack:  packs.secondary_pack,
        face_pack:       packs.face_pack,
        body_pack:       packs.body_pack,
        // UX 5 — labels client (jamais le mot "pack" en clair)
        routine_name:    routineLabel,        // ex : "Routine CLEAR"
        routine_short:   packs.face_pack.replace('pack_', ''), // ex : "CLEAR"
        routine_descriptor: routineDescriptor,// ex : "Acné & Imperfections"
        level:           level,
        niveau_label:    NIVEAU_DISPLAY[level] || 'OPTIMAL',
        // FEATURE 3 — couleurs Routine pour theming
        colors:          routineColors,
        is_safe_gradient: packs.face_pack === 'pack_SAFE',
        phototype:       p.phototype_group,
        is_pregnant:     p.is_pregnant,
        is_breastfeeding:p.is_breastfeeding,
        age_group:       p.age_group,
        tolerance:       p.tolerance,
        uv_exposure:     p.uv_exposure,
        safe_override:   packs.safe_override,
        lifestyle:       p.lifestyle
      },
      immediateAlerts: immediateAlerts,
      block2_products: buildBlock2(selection, p, packs.face_pack),
      block2_header_notice: packs.face_pack === 'pack_SAFE'
        ? 'La Routine SAFE ParaDermia suit une politique de sélection particulièrement stricte : zéro rétinoïde, zéro acide fort, zéro parfum. Chaque produit a été validé par notre pharmacienne.'
        : null,
      block3_routine:  buildBlock3(selection, p),
      block4_alerts:   buildBlock4(p, selection, conflicts, packs),
      // Sprint 4a/4b/4c — Notices cliniques pédagogiques (distinctes des alertes sécurité).
      // Sprint 4d Phase 5 — notice fallback_level conditionnelle ajoutée plus haut.
      clinical_notices: clinicalNotices,
      // Sprint 4c — Flag minimaliste, toujours présent dans output (déterministe UI).
      essentials_only: p.regularite_routine === 'D',
      block5_complements: complements.map(c => ({
        id: c.id,
        brand: c.brand,
        name: c.name,
        category: c._complement_category || 'cure_de_fond',
        role: c._complement_role || 'recommande', // essentiel / recommande / optionnel
        famille_actif: c._famille_actif_key || null, // exposé pour dedup test 72
        notice: c._complement_notice || null,
        pregnancy_notice: (p.is_pregnant || p.is_breastfeeding) ? COMPLEMENT_PREGNANCY_NOTICE : null,
        handle: c.handle || null
      })),
      block6_legal: 'Ce bilan cosmétique est réalisé par une pharmacienne et ne constitue pas un diagnostic médical. En cas de pathologie cutanée, consultez un dermatologue.',
      block7_cta: {
        signature_collection_handle: 'routine-signature-' +
          packs.face_pack.replace('pack_','').toLowerCase() + '-' +
          level.replace('niveau_','').toLowerCase(),
        primary_label:    'Accéder à ma Routine Personnalisée',
        secondary_label:  'Recevoir ma routine par email',
        tertiary_label:   'Recommencer mon analyse de peau'
      },
      body_products: selection.body_products || [],
      // Stratégie homme — blocs add-on barbe/rasage. Toujours présents ([] si
      // non applicable) pour un rendu UI déterministe. Mêmes champs que les
      // autres cartes produit (id/brand/name/handle) ; tags bruts non exposés.
      block_barbe: barbe_products.map(pr => ({ id: pr.id, brand: pr.brand, name: pr.name, handle: pr.handle || null })),
      block_rasage: rasage_products.map(pr => ({ id: pr.id, brand: pr.brand, name: pr.name, handle: pr.handle || null })),
      retinoid_protocol: retProto,
      conflicts: conflicts,
      klaviyo_properties: {
        pack_dominant:   packs.primary_pack,
        face_pack:       packs.face_pack,
        niveau:          level,
        phototype:       p.phototype_group,
        is_pregnant:     p.is_pregnant,
        is_breastfeeding:p.is_breastfeeding,
        mode_vie:        p.lifestyle,
        gender:          p.gender,
        age_group:       p.age_group,
        // Sprint 3 — 9 nouveaux signaux pour segmentage Klaviyo immédiat.
        taches_type:              p.taches_type,
        pih_history:              p.pih_history,
        hormonal_context:         p.hormonal_context,
        tabac:                    p.tabac,
        medicaments:              p.medicaments,
        pratiques_eclaircissants: p.pratiques_eclaircissants,
        regularite_routine:       p.regularite_routine,
        allergies_alimentaires:   p.allergies_alimentaires,
        complements_pris:         p.complements_pris
      }
    };
  }

  // Sprint 2 — Slot traitement (1-3×/sem, jamais essentiel).
  // Active uniquement si profil clinique compatible : activations (any) + exclusions (none).
  function shouldActivateTreatmentSlot(p, packs, level) {
    // Exclusions absolues
    if (packs.face_pack === 'pack_SAFE') return false;
    // AUDIT 2026-06 (corrigé 2026-08-24) — slot GLOW : aucun traitement éclat
    // au catalogue → le slot s'activait sur pool vide et servait le joker
    // Cicaplast (baume réparateur hors sujet), tout en ÉVINÇANT l'exfoliant
    // (skipExfoliant dépend de ce slot) qui est précisément le geste éclat.
    // GLOW ne déclenche plus le slot : l'exfoliant (mandélique/PHA) revient.
    if (packs.face_pack === 'pack_GLOW') return false;
    if (p.post_medical === 'A' || p.post_medical === 'B') return false;
    if (p.barrier_status === 'profil_barriere_fragile' ||
        p.barrier_status === 'profil_barriere_fragile_reactive') return false;
    if (p.tolerance === 'A') return false;
    if (packs.face_pack === 'pack_REPAIR') return false;
    if (level === 'niveau_STARTER') return false;
    if (p.rosacee === true) return false;
    // Activations (au moins une)
    const tolOk = p.tolerance === 'B' || p.tolerance === 'C' || p.tolerance === 'D';
    if (p.score_duree >= 2 && tolOk) return true;
    if (p.score_expertise >= 2) return true;
    if (p.q2_5 === 'C') return true;
    if (packs.face_pack === 'pack_LIFT' || packs.face_pack === 'pack_EVEN') return true;
    return false;
  }

  function buildActiveSteps(p, packs, level) {
    let steps = [];
    if (p.port_maquillage) steps.push('etape_demaquillant');
    steps.push('etape_nettoyant');
    steps.push('etape_toner');
    // Exfoliant exclu : SAFE (zéro exfoliant chimique), post-acte actif,
    // barrière fragile/réactive (toute routine — peau non stabilisée → skip),
    // tolérance très réactive, OU présence d'un traitement intensif (un seul
    // soin actif fort par soir : pas d'empilement exfoliant + traitement).
    // HANDOFF audit : élargi vs REPAIR+fragile seul.
    const barrierFragile = p.barrier_status === 'profil_barriere_fragile' ||
                           p.barrier_status === 'profil_barriere_fragile_reactive';
    const skipExfoliant =
      packs.face_pack === 'pack_SAFE' ||
      p.post_medical === 'A' ||
      barrierFragile ||
      p.tolerance === 'A' ||
      p.skin_type === 'profil_peau_seche' ||   // V3.34f — peau sèche = pas d'exfoliant régulier
      shouldActivateTreatmentSlot(p, packs, level);
    if (!skipExfoliant) steps.push('etape_exfoliant');
    steps.push('etape_serum');
    // Sprint 2 — Slot traitement conditionnel après sérum.
    if (shouldActivateTreatmentSlot(p, packs, level)) steps.push('etape_traitement');
    steps.push('etape_hydratant');
    steps.push('etape_spf');

    // Sprint 4c — Simplification de la routine selon régularité déclarée.
    // Appliquée EN FIN, filtre final sur les étapes calculées par les règles standards.
    //
    // V9.2 (correction clinique 2026-08-29, demande fondatrice) — ces règles
    // allégeaient la routine en retirant AUSSI le sérum quand la régularité
    // déclarée était faible. Conséquence constatée en test réel : un profil
    // « Rides » qui répond « occasionnellement » recevait nettoyant + hydratant
    // + SPF, soit ZÉRO actif traitant sa préoccupation — le diagnostic ne
    // servait plus à rien. Règle désormais appliquée : on n'enlève jamais
    // l'actif. Ce qu'on allège, ce sont les gestes ADDITIONNELS (exfoliant,
    // traitement à fréquence spécifique). Le sérum — le geste ciblé au meilleur
    // rapport bénéfice/effort — et le toner restent proposés ; côté page
    // résultats le toner tombe dans le groupe « Confort » (replié, non coché),
    // donc il est offert sans être imposé.
    const dropAdditionnels = new Set(['etape_exfoliant', 'etape_traitement']);
    // R3.3 — Quelques fois par semaine.
    // R3.4 — Occasionnellement : même socle, la notice clinique
    // `regularite_minimaliste` porte le message de simplification.
    if (p.regularite_routine === 'C' || p.regularite_routine === 'D') {
      steps = steps.filter(s => !dropAdditionnels.has(s));
    }
    return steps;
  }

  // BLOCK 2 — cartes produit. BUG 3 : aucun tag interne ne fuit vers l'UI,
  // seuls les actifs sont exposés via libellés propres (map ACTIF_DISPLAY).
  function buildBlock2(selection, p, pack) {
    const out = [];
    for (const step of STEP_ORDER) {
      const prod = selection.products[step];
      if (!prod) continue;
      const tier = (prod.tiers && prod.tiers[pack]) || null;
      const isTreatment = step === 'etape_traitement';
      out.push({
        step: step,
        id: prod.id,
        brand: prod.brand,
        name: prod.name,
        rank: tier,
        role: prod.role || null,
        // actifs affichés = libellés humains, jamais les tags bruts
        actifs_display: displayActifs(prod.actifs),
        handle: prod.handle || null,
        notices: productNotices(prod, p, selection, pack),
        // Sprint 2 — Slot traitement : marquage UI pour rendu différencié.
        is_optional: isTreatment,
        frequency_hint: isTreatment
          ? '2 à 3 fois par semaine, en alternance avec votre sérum'
          : null,
        // is_joker non exposé à l'UI — garde l'info interne pour logs éventuels
        _is_joker: !prod.packs || prod.packs.indexOf(pack) === -1
      });
    }
    return out;
  }

  // Timeline matin/soir — BUG 1 fix.
  // Chaque produit apparaît 0 ou 1 fois par colonne, jamais plus.
  // Règles :
  //   - SPF → matin uniquement (jamais soir)
  //   - exfoliant avec contient_acide_fort → soir uniquement
  //   - rétinoïde (rétinal, rétinol, famille_actif_retinoide) → soir uniquement
  //   - usage_soir → soir uniquement
  //   - usage_matin → matin uniquement
  //   - usage_matin_soir (ou usage absent) → matin + soir
  function buildBlock3(selection, p) {
    const morning = [], evening = [];
    const products = Object.entries(selection.products);
    const orderIdx = (step) => STEP_ORDER.indexOf(step);
    products.sort((a, b) => orderIdx(a[0]) - orderIdx(b[0]));

    const pushOnce = (list, step, prod) => {
      if (list.some(it => it.product.id === prod.id)) return;
      list.push({ step, product: brief(prod) });
    };

    for (const [step, prod] of products) {
      if (!prod) continue;

      // 1. Règles spécifiques par étape (priorité absolue).
      if (step === 'etape_spf') { pushOnce(morning, step, prod); continue; }
      // Sprint 2 — Slot traitement : soir strict (frequency_hint UI gère l'alternance).
      if (step === 'etape_traitement') { pushOnce(evening, step, prod); continue; }
      if (step === 'etape_exfoliant' && hasTag(prod, 'contient_acide_fort')) {
        pushOnce(evening, step, prod); continue;
      }
      // 2. Rétinoïdes → soir strict.
      if (hasAny(prod, ['contient_retinol','actif_retinal','actif_retinol','famille_actif_retinoide'])) {
        pushOnce(evening, step, prod); continue;
      }

      // 3. Lecture stricte du tag usage_*.
      const usage = (prod.usage || '').toLowerCase();
      if (usage === 'usage_matin_soir' || !usage) {
        pushOnce(morning, step, prod);
        pushOnce(evening, step, prod);
      } else if (usage === 'usage_matin') {
        pushOnce(morning, step, prod);
      } else if (usage === 'usage_soir') {
        pushOnce(evening, step, prod);
      } else {
        // fallback (usage inconnu) : matin + soir
        pushOnce(morning, step, prod);
        pushOnce(evening, step, prod);
      }
    }
    return { morning, evening };
  }

  // Alertes BLOCK 4 — icon = clé symbolique (SVG côté UI), jamais un emoji.
  // Sprint 4b — Format étendu {icon, text, severity?} : severity 'high' réservé
  // aux alertes nécessitant action utilisatrice (consultation derma, etc.).
  // Champ omis pour les alertes informatives existantes (compat backward).
  function buildBlock4(p, selection, conflicts, packs) {
    const out = [];
    if (p.is_pregnant || p.is_breastfeeding) {
      out.push({ icon: 'pregnancy', text: 'Routine conçue pour la grossesse et l\'allaitement — filtres INCI stricts appliqués.' });
    }
    if (p.phototype_group === 'phototype_IV_V_VI') {
      out.push({ icon: 'sun_strong', text: 'SPF renforcé recommandé — phototype IV-VI : le SPF est un actif traitement à part entière.' });
    }
    if (p.uv_exposure === 'forte') {
      const leaveOnSensitive = Object.values(selection.products).some(pr => pr && hasAny(pr, ['INCI_EO_Phototoxic','contient_acide_fort','contient_retinol']));
      if (leaveOnSensitive) out.push({ icon: 'warning', text: 'Actifs photosensibilisants détectés — usage soir strict obligatoire.' });
    }
    conflicts.forEach(c => out.push({ icon: 'warning', text: c.notice }));
    if (p.post_medical === 'A') {
      out.push({ icon: 'warning', text: 'Rétinol déconseillé en période de récupération post-acte médical récent.' });
    }
    // Sprint 4b R4.2 — Éclaircissants sans prescription : alerte sévérité élevée
    // recommandant consultation dermatologique avant la poursuite.
    if (p.pratiques_eclaircissants === 'B') {
      out.push({
        icon: 'warning',
        severity: 'high',
        text: 'Certains produits éclaircissants en vente libre contiennent des actifs à risque (hydroquinone non encadrée, corticoïdes topiques). Nous recommandons fortement une consultation dermatologique avant la poursuite. Cette routine est calibrée pour minimiser les risques de cumul.'
      });
    }
    // Sprint 4d Phase 4 R2 — Rasage régulier (Q7_9='A') sur peau à risque
    // pigmentaire : alerte PFB (pseudofolliculitis barbae) + risque PIH renforcé.
    // Trigger sur phototype_at_risk_pih qui couvre IV-VI directement et III + pih
    // ∈ {A,B,D}. Severity 'high' parce que la combinaison PFB + tendance PIH
    // est cliniquement significative et mérite que la cliente soit informée.
    if (p.rasage === 'A' && p.phototype_at_risk_pih) {
      out.push({
        icon: 'warning',
        severity: 'high',
        text: 'Le rasage régulier sur peau à risque pigmentaire peut entraîner poils incarnés et taches. Cette routine privilégie les actifs apaisants et exfoliants doux pour limiter ces inconforts.'
      });
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  Sprint 4a/4b/4c — Notices cliniques pédagogiques
  //  Format {type, text}. Distinctes des alertes sécurité (severity) et des
  //  notices produit (productNotices). Affichées en section pédagogique dédiée.
  // ────────────────────────────────────────────────────────────────────────────
  function buildClinicalNotices(p, packs, level, answers) {
    const notices = [];
    const a = answers || {};

    // ─── Sprint 4a — Pigmentation / PIH / hormonal × pigmentation ───
    if (p.hormonal_context === 'D' && a.Q2_3 === 'B') {
      notices.push({ type: 'thm_melasma',
        text: 'Le traitement hormonal de la ménopause peut entretenir le mélasma. Une discussion avec votre médecin sur la formulation hormonale est pertinente si la pigmentation est invalidante.' });
    }
    if (p.taches_type === 'B') {
      notices.push({ type: 'melasma_photoprotection',
        text: 'Le mélasma est récidivant. La photoprotection rigoureuse SPF 50+, toute l\'année et réappliquée toutes les deux heures en exposition, est nécessaire pour maintenir les résultats.' });
    }
    if (p.hormonal_context === 'A' && a.Q2_3 === 'B') {
      notices.push({ type: 'pilule_melasma',
        text: 'Le mélasma sous contraception orale est documenté cliniquement. Une discussion avec votre médecin gynécologue sur l\'option contraceptive peut être pertinente si la pigmentation est gênante. Cette routine atténue en gardant votre choix actuel.' });
    }
    if (p.taches_type === 'D') {
      notices.push({ type: 'taches_type_unknown',
        text: 'Une consultation dermatologique permettrait de préciser le type de taches pour une stratégie plus ciblée. Cette routine couvre les cas généraux sans risque d\'aggravation.' });
    }
    if (p.pih_strict_active) {
      notices.push({ type: 'pih_strict_active',
        text: 'Votre tendance à pigmenter après inflammation a guidé la sélection. Les actifs ont été choisis pour minimiser tout risque de nouvelles taches.' });
    }

    // ─── Sprint 4b — TTC + médicaments + pratiques éclaircissants ───
    if (p.hormonal_context === 'F') {
      notices.push({ type: 'ttc_teratogenes',
        text: 'Pour préserver votre projet de conception, votre routine évite les actifs déconseillés en cas de grossesse, par précaution préventive.' });
    }
    if (hasMedicament(p, 'A')) {
      notices.push({ type: 'isotretinoine_recovery',
        text: 'Votre traitement d\'isotrétinoïne demande une période de récupération avant introduction d\'actifs concentrés. Consultez votre dermatologue avant toute évolution de routine.' });
    }
    if (hasMedicament(p, 'B')) {
      notices.push({ type: 'anticoag_prudence',
        text: 'Vos anticoagulants demandent prudence avec les actifs exfoliants intensifs. Cette routine privilégie les actifs doux.' });
    }
    if (hasMedicament(p, 'C')) {
      notices.push({ type: 'diabete_reparateur',
        text: 'Le diabète peut altérer la cicatrisation cutanée. Votre routine privilégie les actifs réparateurs et apaisants.' });
    }
    if (hasMedicament(p, 'D')) {
      notices.push({ type: 'thyroide_hydratation',
        text: 'Un dérèglement thyroïdien peut entraîner sécheresse et sensibilité. Votre routine renforce l\'hydratation et la barrière.' });
    }
    if (hasMedicament(p, 'E')) {
      notices.push({ type: 'antibio_microbiote',
        text: 'Une antibiothérapie prolongée altère le microbiote. Un complément probiotique est suggéré pour soutenir l\'équilibre.' });
    }
    if (hasMedicament(p, 'F')) {
      notices.push({ type: 'autre_traitement',
        text: 'Vous suivez un autre traitement médical. Mentionnez votre routine à votre médecin pour confirmer la compatibilité avec les actifs sélectionnés.' });
    }
    if (p.pratiques_eclaircissants === 'A') {
      notices.push({ type: 'eclaircissants_prescrits',
        text: 'Continuez à suivre les recommandations de votre dermatologue. Nos actifs complémentaires ont été choisis pour éviter tout cumul.' });
    }

    // ─── Sprint 4c — Tabac + régularité + compléments + acné femme adulte ───
    if (p.tabac === 'A') {
      notices.push({ type: 'tabac_oxydatif',
        text: 'Le tabagisme actif accélère le vieillissement cutané et le stress oxydatif. Votre routine renforce les défenses antioxydantes et la photoprotection.' });
    }
    if (p.regularite_routine === 'C') {
      notices.push({ type: 'regularite_simplifie',
        text: 'Cette routine est ajustée à votre rythme actuel. Le toner et l\'exfoliant ont été retirés pour rester réaliste, vous pourrez les introduire progressivement.' });
    }
    if (p.regularite_routine === 'D') {
      notices.push({ type: 'regularite_minimaliste',
        text: 'Voici un protocole minimaliste réaliste avec votre rythme actuel. Vous pourrez l\'enrichir progressivement quand votre routine sera plus régulière.' });
    }
    // Compléments déjà pris (array non vide, et différent de [J] seul)
    const compl = Array.isArray(p.complements_pris) ? p.complements_pris : [];
    if (compl.length && !(compl.length === 1 && compl[0] === 'J')) {
      notices.push({ type: 'complements_dedoublonnage',
        text: 'Les compléments que vous prenez déjà ont été pris en compte pour éviter tout doublon dans nos recommandations.' });
    }
    if (p.hormonal_context === 'A' && a.Q2_3 === 'A') {
      notices.push({ type: 'acne_femme_adulte_hormonal',
        text: 'L\'acné de la femme adulte sous contraception orale est souvent multifactorielle, à composante hormonale et inflammatoire. Cette routine cible particulièrement les composantes inflammatoires et anti-PIH.' });
    }

    return notices;
  }

  function brief(p) {
    return { id: p.id, brand: p.brand, name: p.name, handle: p.handle || null };
  }

  // ────────────────────────────────────────────────────────────────────────────
  //  EXPORT
  // ────────────────────────────────────────────────────────────────────────────

  const ParaDermiaAlgo = {
    run,
    collectInputs,
    computeLevel,
    computeAlgoLevel,
    computeRecommendedLevel,
    maxAllowedLevel,
    determinePack,
    computeImmediateAlerts,
    buildStepPool,
    pickWithCascade,
    selectComplements,
    retinoidProgression,
    productNotices,
    resolveActiveConflicts,
    shouldActivateTreatmentSlot,
    buildClinicalNotices,
    // constants exported for tests
    JOKERS: JOKER_HINTS, // V3.34f — renommé en JOKER_HINTS (alias export conservé)
    PACKS,
    LEVELS,
    STEP_ORDER,
    PREGNANCY_EXCLUDES
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = ParaDermiaAlgo;
  } else {
    root.ParaDermiaAlgo = ParaDermiaAlgo;
  }
})(typeof window !== 'undefined' ? window : globalThis);
